/**
 * viewport.js — Core Viewport Manager (модульная версия) v7.0.61
 * Mikhmel Cornerstone4D DICOM Viewer Pro v6
 *
 * [v7.0.61 FIXES]:
 *  - planRecovery: передаётся activeViewports
 *  - _computeHistogram: AbortController для остановки при destroy
 *  - _camRAF: отменяется при destroy
 *  - _contextLossHandlers: Map вместо WeakSet, снимаются при cleanupViewports/destroy
 *  - _getCurrentVolumeIdFromViewports: проверка всех возможных полей uid
 *  - _reinstallOverlays: immediateRender=false (4 аргумента)
 *  - _snapshotViewports: защита от setProperties(undefined)
 *
 * Этот файл содержит ТОЛЬКО core-логику:
 *   - setupViewports, loadStack, loadVolume, switchViewMode, destroy
 *   - setWindowLevel, resetViewport, zoomViewport
 *   - set3DPreset, applyPresetToAll
 *   - Cinematic, Hanging Protocols, Adaptive Quality, Overlay
 *   - Global handlers (resize, visibility, camera sync)
 */

import state, {
  getRenderingEngine, getViewportIds, getViewportElement, find3DViewportId,
  getActiveOrFirstViewportId, setHoveredViewportId,
  getCinematicRenderer, setCinematicRenderer,
  cornerstone, cornerstoneTools,
  RenderingEngine, ViewportType, OrientationAxis, CONSTANTS,
  RENDERING_ENGINE_ID, TOOL_GROUP_ID, DEFAULT_3D_PRESET, DEFAULT_3D_VIEW,
  LAYOUTS, VP_TITLES, find3DViewportIds, isMprLayout, sliderXExpr,
  setWindowLevel,
} from './viewportState.js';

import { memoryGuardian, setVolumeReclaimer } from '../managers/MemoryGuardian.js';
import { planRecovery, verifyRecovery } from './recoveryPlanCore.js';
// [10.0.6] Одно правило прореживания на ВСЕ пути постройки объёма — см. шапку модуля.
import { planSliceBudget, thinByStep } from './sliceBudget.js';
// [10.19.14] Нормировка МРТ по гистограмме серии: без неё пункты «МР …» в списке
// «Перенос» были бы обманом — у МРТ нет единиц Хаунсфилда.
import { окноСерии as окноСерииМР, переносДляСерии as переносМРДляСерии } from './mrNormalize.js';
import { logRecovery, showRecoveryChip, showRecoveryReport, getRecoveryReport, shouldNotify, noteMemoryEvent } from '../ui/recoveryReport.js';
// [9.0.0] Что было открыто — чтобы после выгрузки вкладки предложить вернуться.
import { noteVolumeShown } from './sessionState.js';
import {
  rememberVolume, forgetVolume, forgetAllVolumes, getVolumeRecord,
  planRestore, allImageIds, canonicalId, volumeCount, setEvictionHandler, knownVolumeIds,
} from './volumeRegistry.js';

// [9.0.1] Что отдать, когда память кончается, а освободить нечего.
//
// Отдаём тома, которые лежат в кэше, но НЕ показаны ни в одном вьюпорте: на
// них никто не смотрит, а вернуть их мы теперь умеем — записи в реестре есть.
// Показанные не трогаем ни при каких обстоятельствах: чернить экран, спасая
// память, значит менять одну беду на другую.
//
// Возвращаем число сброшенных томов — «Страж» пишет его в отчёт.
setVolumeReclaimer((keepIds = []) => {
  let dropped = 0;
  try {
    const shown = new Set(_visibleVolumeIds());
    // Упреждающий вызов может попросить сохранить том, который нарочно ещё
    // не показан — собираемый фоном для текущей серии.
    for (const k of keepIds) { shown.add(String(k).replace(/^cornerstoneStreamingImageVolume:/, '')); }
    for (const id of knownVolumeIds()) {
      if (shown.has(id)) continue;
      const rec = getVolumeRecord(id);
      if (!rec) continue;
      const fullId = id.startsWith('cornerstoneStreamingImageVolume:') ? id : `cornerstoneStreamingImageVolume:${id}`;
      try {
        if (!cornerstone.cache.getVolume?.(fullId) && !cornerstone.cache.getVolume?.(id)) continue;
        cornerstone.cache.removeVolumeLoadObject(fullId);
        dropped++;
        // Запись в реестре НЕ удаляем: она и нужна, чтобы вернуть том обратно.
        // Снимаем только защиту с кадров — иначе они так и останутся мёртвым
        // грузом в списке защищённых.
        memoryGuardian.unlock(rec.imageIds);
      } catch (e) { log.debug('[reclaim]', e.message); }
    }
  } catch (e) { log.debug('[reclaim]', e.message); }
  if (dropped) log.warn(`[reclaim] память кончалась — сброшено непоказанных томов: ${dropped}`);
  return dropped;
});

setEvictionHandler((rec) => {
  try { if (rec?.imageIds?.length) memoryGuardian.unlock(rec.imageIds); } catch (e) { /* */ }
});
import { apply3DPreset, getDefaultForModality, applyWorkflow, getPreset3D, addCustomPreset } from './presetsManager.js';
import { autoPreset3D } from './autoPreset3D.js';   // [10.0.2] пресет второго объёмного окна mpr6
import { computeFit3D, distanceForFit } from './fit3D.js';   // [10.0.2] вписывание объёма в окно
import { planPresetApply } from './presetPlan.js';   // [10.0.2] кому адресован пресет
import { planPresetRange, rescalePreset } from './presetRange.js';   // [10.0.3] перенос пресета на шкалу серии
import { getRecommendedBatchSize, getGPUInfo } from './gpuDetector.js';
import { CinematicRenderer, createCinematicPanel } from '../rendering/cinematicRendering.js';
import { attachDepthExtractor, detachDepthExtractor } from '../rendering/depthExtractor.js';
import { autoSelectPreset } from '../rendering/cinematicAutoPreset.js';
import { events } from './events.js';
import { EVENTS } from './eventBus.js';
import { registerAPIs } from './viewerAPI.js';
import { showToast } from '../utils/helpers.js';
import { refreshGridOverlays } from './gridOverlay.js';
import { log } from '../utils/logger.js';
import { initTools, activateDataTools } from '../tools/toolManager.js';

// ── Sub-module imports ──
import { setStandardView, pushCameraState, popCameraState, getCameraHistoryLength, redoCameraState, getCameraRedoLength, getStandardViews, animateCameraTo, saveCameraPreset, listCameraPresets, deleteCameraPreset, setCameraA, setCameraB, startAutoRotate, stopAutoRotate, isAutoRotating, flyAlongPath, adjustCamera3D, rotateCameraRoll, animateToStandardView, loadCameraPreset, toggleCameraAB, setStandardView as _setStdView } from './viewportCamera.js';
import { startCine, stopCine, getCineState, setCineSpeed, pauseCine, resumeCine, toggleCine as toggleCinePlayback, setCineRange, seekCine, getCineStats } from './viewportCine.js';
import { screenshotViewport, downloadScreenshot, screenshotAll, exportDICOMSecondaryCapture, downloadDICOMCapture, copyScreenshotToClipboard, createComparisonStrip, batchExportSlices } from './viewportScreenshot.js';
import { enableHUProbe, disableHUProbe, computeCircleROIStats, computeRectROIStats, classifyTissue, getTissueTable, computeLineProfile, computeTissueDensityMap, computeCalciumScore, jumpToPathology, computeVolumeStats } from './viewportAnalysis.js';
import { enableMPRSync, disableMPRSync, enablePositionSync, disablePositionSync, jumpToWorldPosition, stopSmartCompare, startSmartCompare } from './viewportSync.js';
import { init4DViewer, start4DPlayback, stop4DPlayback, get4DState, detectCardiacPhases, set4DSpeed, getPhaseTimeline, setCardiacPhase, nextCardiacPhase, prevCardiacPhase, jumpToSystole, jumpToDiastole, preloadAllPhases } from './viewport4D.js';
import { setSlabProjection, getSlabState, cycleSlabProjection, getVolumeClipping, animateWindowLevel, adjustSlabThickness, getSlabPresets, applySlabPreset, getClipPresets, applyClipPreset, resetClipping, getDualEnergyModes, setVolumeClipping, animatePresetTransition, applyDualEnergyMode } from './viewportEffects.js';
import { toggleMagnifier, enableMagnifier, disableMagnifier, toggleRulerGrid, showRulerGrid, removeRulerGrid, showInfoOverlay, hideInfoOverlay, addBookmark, goToBookmark, removeBookmark, getBookmarks, navigateBookmarks, enableGestures, disableGestures, enableAllGestures, showCrosshairOverlay, hideCrosshairOverlay, toggleCrosshairOverlay, showPictureInPicture, hidePictureInPicture } from './viewportUI.js';
import { enableKeyboardShortcuts, disableKeyboardShortcuts, addKeyboardShortcut, listKeyboardShortcuts, getWLPresets, applyWLPreset, autoWindowLevel } from './viewportKeyboard.js';
import { startSessionRecording, stopSessionRecording, replaySession, exportSessionJSON, exportSessionHTML, enableAnnotationAutoSave, startStudyTimer, pauseStudyTimer, resumeStudyTimer, stopStudyTimer, getStudyTimerElapsed, saveAnnotation, loadAnnotations, deleteAnnotation, clearAnnotations, exportAnnotationsJSON, importAnnotationsJSON, getAnnotationStats } from './viewportPersistence.js';
import { enableVoiceControl, disableVoiceControl, addVoiceCommand, listVoiceCommands, isVoiceActive, getVoiceHistory } from './viewportVoice.js';export {
  getRenderingEngine, getViewportIds, getViewportElement, LAYOUTS,
  pushCameraState, popCameraState, getCameraHistoryLength, setStandardView,
  redoCameraState, getCameraRedoLength, getStandardViews, animateCameraTo, animateToStandardView,
  saveCameraPreset, loadCameraPreset, listCameraPresets, deleteCameraPreset,
  setCameraA, setCameraB, toggleCameraAB, startAutoRotate, stopAutoRotate, isAutoRotating, flyAlongPath,
  adjustCamera3D, rotateCameraRoll,
  startCine, stopCine, getCineState, setCineSpeed, pauseCine, resumeCine, toggleCinePlayback, setCineRange, seekCine, getCineStats,
  screenshotViewport, downloadScreenshot, screenshotAll, exportDICOMSecondaryCapture, downloadDICOMCapture,
  copyScreenshotToClipboard, createComparisonStrip, batchExportSlices,
  enableHUProbe, disableHUProbe, computeCircleROIStats, computeRectROIStats, computeCalciumScore, jumpToPathology,
  classifyTissue, getTissueTable, computeLineProfile, computeTissueDensityMap, computeVolumeStats,
  enableMPRSync, disableMPRSync, enablePositionSync, disablePositionSync, jumpToWorldPosition, startSmartCompare, stopSmartCompare,
  init4DViewer, setCardiacPhase, nextCardiacPhase, prevCardiacPhase, start4DPlayback, stop4DPlayback, get4DState, detectCardiacPhases,
  jumpToSystole, jumpToDiastole, set4DSpeed, getPhaseTimeline, preloadAllPhases,
  setSlabProjection, getSlabState, cycleSlabProjection, setVolumeClipping, getVolumeClipping, animateWindowLevel, animatePresetTransition,
  adjustSlabThickness, getSlabPresets, applySlabPreset, getClipPresets, applyClipPreset, resetClipping, getDualEnergyModes, applyDualEnergyMode,
  toggleMagnifier, enableMagnifier, disableMagnifier, toggleRulerGrid, showRulerGrid, removeRulerGrid, showInfoOverlay, hideInfoOverlay,
  addBookmark, goToBookmark, removeBookmark, getBookmarks, navigateBookmarks, enableGestures, disableGestures, enableAllGestures,
  showCrosshairOverlay, hideCrosshairOverlay, toggleCrosshairOverlay, showPictureInPicture, hidePictureInPicture,
  enableKeyboardShortcuts, disableKeyboardShortcuts, addKeyboardShortcut, listKeyboardShortcuts, getWLPresets, applyWLPreset, autoWindowLevel,
  startSessionRecording, stopSessionRecording, replaySession, exportSessionJSON, exportSessionHTML,
  saveAnnotation, loadAnnotations, deleteAnnotation, clearAnnotations, exportAnnotationsJSON, importAnnotationsJSON, enableAnnotationAutoSave,
  getAnnotationStats, startStudyTimer, pauseStudyTimer, resumeStudyTimer, stopStudyTimer, getStudyTimerElapsed,
  enableVoiceControl, disableVoiceControl, addVoiceCommand, listVoiceCommands, isVoiceActive, getVoiceHistory,
};

// ══════════════════════════════════════════════════════════════
//  INTERNAL HELPERS
// ══════════════════════════════════════════════════════════════

let _vtkPlanePromise = null;
/**
 * [11.1.26] Экспортирован: «Убрать стол» (sidebarBindings) строит плоскость
 * отсечения тем же путём, что и остальной код объёма. Первая моя редакция
 * искала vtkPlane в window.vtk — глобали в сборке нет, и выключатель отказал
 * бы словами «недоступно в этой сборке», хотя всё на месте.
 */
export function getVtkPlaneClass() { return _getVtkPlane(); }
function _getVtkPlane() {
  if (!_vtkPlanePromise) _vtkPlanePromise = import('@kitware/vtk.js/Common/DataModel/Plane').then(m => m.default || m).catch(() => null);
  return _vtkPlanePromise;
}
_getVtkPlane();

function _initCinematic(vp3d) {
  if (!vp3d) return;
  try {
    if (state.cinematicRenderer) { try { detachDepthExtractor(); } catch (e) { log.debug("[viewport]", e.message); } try { state.cinematicRenderer.dispose(); } catch { /* */ } }
    const cr = new CinematicRenderer(vp3d, { preset: 'cinematic' });
    setCinematicRenderer(cr);
    // 10.19.18: подключение глубины стало асинхронным (объём читается из кэша
    // Cornerstone). Ошибку глотать нельзя — но и ждать её здесь незачем:
    // окно должно открыться, даже если глубину посчитать не удалось.
    Promise.resolve(attachDepthExtractor(vp3d, cr)).catch((e) => log.warn('[Глубина]', e.message));
    try { document.getElementById('mnav-cinematic')?.classList.add('cinematic-ready'); } catch (e) { log.debug("[viewport]", e.message); }
  } catch (e) {
    setCinematicRenderer(null);
    log.warn('[Cinematic] Init failed:', e.message);
  }
}

let _sharedRO = null;
const _roCallbacks = new Map();

function _getSharedRO() {
  if (!_sharedRO) {
    _sharedRO = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.contentRect.width > 0 && entry.contentRect.height > 0) {
          const cb = _roCallbacks.get(entry.target);
          if (cb) { _roCallbacks.delete(entry.target); _sharedRO.unobserve(entry.target); cb(); }
        }
      }
    });
  }
  return _sharedRO;
}

function awaitableResize(re, containerEl, timeoutMs = 1000) {
  return new Promise((resolve) => {
    const doResize = () => { try { re.resize(true, true); } catch (e) { log.warn('[VP] resize:', e.message); } resolve(); };
    if (containerEl?.offsetWidth > 0 && containerEl?.offsetHeight > 0) {
      requestAnimationFrame(() => requestAnimationFrame(doResize));
    } else {
      if (containerEl) { _getSharedRO(); _roCallbacks.set(containerEl, () => requestAnimationFrame(doResize)); _sharedRO.observe(containerEl); }
      setTimeout(() => { if (containerEl) { _roCallbacks.delete(containerEl); try { _sharedRO?.unobserve(containerEl); } catch (e) { log.debug("[viewport]", e.message); } } doResize(); }, timeoutMs);
    }
  });
}

// [v7.0.61] Контекст-обработчики с возможностью снятия (Map вместо WeakSet)
const _contextLossHandlers = new Map();

function _attachContextLossHandlers() {
  const re = state.renderingEngine;
  if (!re) return;
  for (const vp of (re.getViewports() || [])) {
    const canvas = vp.canvas || vp.element?.querySelector('canvas');
    if (!canvas || _contextLossHandlers.has(canvas)) continue;
    
    const lostHandler = (e) => { e.preventDefault(); _glLost = true; _glLostSinceLastRecover = true; showToast('⚠️ GPU контекст потерян', 'warning', 3000); };
    const restoredHandler = () => { _glLost = false; _recoverAfterBackground('контекст GPU восстановлен'); };
    
    canvas.addEventListener('webglcontextlost', lostHandler);
    canvas.addEventListener('webglcontextrestored', restoredHandler);
    
    _contextLossHandlers.set(canvas, { lost: lostHandler, restored: restoredHandler });
  }
}

function _detachContextLossHandlers() {
  for (const [canvas, handlers] of _contextLossHandlers) {
    canvas.removeEventListener('webglcontextlost', handlers.lost);
    canvas.removeEventListener('webglcontextrestored', handlers.restored);
  }
  _contextLossHandlers.clear();
}

function cleanupViewports(re) {
  if (state.viewports.size === 0) return;
  if (state.cinematicRenderer) {
    try { detachDepthExtractor(); } catch (e) { log.debug("[viewport]", e.message); } try { state.cinematicRenderer.dispose(); } catch { /* */ }
    setCinematicRenderer(null);
    try { document.getElementById('mnav-cinematic')?.classList.remove('cinematic-ready', 'active'); } catch (e) { log.debug("[viewport]", e.message); }
  }
  
  _detachContextLossHandlers();
  
  const oldIds = [...state.viewports.keys()];
  for (const vpId of oldIds) {
    try { const vp = re.getViewport(vpId); if (vp?.element) re.disableElement(vp.element); } catch (e) { log.debug("[viewport]", e.message); }
  }
  state.viewports.clear();
}

let _glLost = false;
let _glLostSinceLastRecover = false;

// [v7.0.61] _camRAF с возможностью отмены
let _camRAF = null;
function _onCameraModified() {
  if (_camRAF) return;
  _camRAF = requestAnimationFrame(() => {
    _camRAF = null;
    const re = state.renderingEngine;
    if (re) { const ids = getViewportIds(); if (ids.length > 1) try { re.renderViewports(ids); } catch (e) { log.debug("[viewport]", e.message); } }
  });
}

function _cancelCamRAF() {
  if (_camRAF) {
    cancelAnimationFrame(_camRAF);
    _camRAF = null;
  }
}

try {
  const CAMERA_EVT = cornerstone.Enums?.Events?.CAMERA_MODIFIED || 'CORNERSTONE_CAMERA_MODIFIED';
  cornerstone.eventTarget.addEventListener(CAMERA_EVT, _onCameraModified);
} catch (e) { log.debug("[viewport]", e.message); }

// [FIX] AbortController для очистки глобальных слушателей
let _ac = null;

// ══════════════════════════════════════════════════════════════
//  CORE PUBLIC API
// ══════════════════════════════════════════════════════════════

/**
 * [10.0.3] Слой управления окном: масштаб по нижним углам, поворот — по
 * правому верхнему (только у объёмных).
 *
 * Углы задаются атрибутом data-corner, раскладка — в CSS. Так одно и то же
 * место кнопки получается и здесь, и в одиночном режиме (switchViewMode),
 * который раньше строил свой собственный вариант.
 *
 * @param {string} vpId
 * @param {boolean} [is3D] переопределить определение по имени окна
 */
/**
 * [10.0.3] В каких числах лежит серия — по выборке, а не по всему массиву.
 *
 * Берём не абсолютные минимум и максимум, а 1-й и 99-й процентили: одиночный
 * выброс (маркер, артефакт металла) растянул бы диапазон вдвое и увёл перенос
 * пресета в пустоту.
 */
function _scanValueRange(scalarData) {
  try {
    const n = scalarData.length;
    if (!n) return null;
    const step = n > 2000000 ? Math.ceil(n / 2000000) : 1;
    const sample = [];
    for (let i = 0; i < n; i += step) {
      const v = scalarData[i];
      if (Number.isFinite(v)) sample.push(v);
    }
    if (sample.length < 32) return null;
    sample.sort((a, b) => a - b);
    const at = (q) => sample[Math.min(sample.length - 1, Math.max(0, Math.floor(sample.length * q)))];
    const lo = at(0.01), hi = at(0.99);
    return (hi > lo) ? { lo, hi } : null;
  } catch (e) { log.debug('[3D] диапазон серии:', e.message); return null; }
}

/** Модальность текущей серии — для решения, можно ли двигать шкалу пресета. */
let _volumeValueRange = null;
let _volumeModality = '';

function _buildViewportControls(vpId, is3D) {
  const volume = (typeof is3D === 'boolean')
    ? is3D
    : (vpId === 'view-3d' || vpId === 'volume3d' || String(vpId).includes('3d'));

  const layer = document.createElement('div');
  layer.className = 'vp-zoom-spread';

  const mk = (corner, title, glyph, extraClass) => {
    const b = document.createElement('button');
    b.className = 'vp-zoom-round' + (extraClass ? ' ' + extraClass : '');
    b.dataset.corner = corner;
    b.title = title;
    b.setAttribute('aria-label', title);
    b.textContent = glyph;
    layer.appendChild(b);
    return b;
  };

  const btnOut = mk('bl', 'Отдалить', '−');
  const btnIn = mk('br', 'Приблизить', '+');
  btnOut.addEventListener('pointerdown', e => { e.stopPropagation(); zoomViewport(vpId, 'out'); });
  btnIn.addEventListener('pointerdown', e => { e.stopPropagation(); zoomViewport(vpId, 'in'); });

  if (volume) {
    const btnRotLeft = mk('tr', 'Повернуть влево (Roll)', '⟲', 'vp-rotate-left');
    const btnRotRight = mk('tr2', 'Повернуть вправо (Roll)', '⟳', 'vp-rotate-right');
    btnRotLeft.addEventListener('pointerdown', e => { e.stopPropagation(); adjustCamera3D(vpId, 0, 0, -1, true); });
    btnRotRight.addEventListener('pointerdown', e => { e.stopPropagation(); adjustCamera3D(vpId, 0, 0, 1, true); });
  }

  return layer;
}

export async function setupViewports(containerId, layout = '1x1') {
  try {
    _bindLiveSliceOverlay();
    [60, 300, 1200].forEach(ms => setTimeout(refreshGridOverlays, ms));
    const container = document.getElementById(containerId);
    if (!container) throw new Error(`Container #${containerId} not found`);
    const re = getRenderingEngine();
    const config = LAYOUTS[layout];
    if (!config) throw new Error(`Unknown layout: ${layout}`);
  
    cleanupViewports(re);

    // [10.0.2] Сетка под форму экрана.
    //
    // Шесть окон 2×3 на телефоне в портрете дают колонку шириной 120 px —
    // в ней не видно ни среза, ни подписи. Разворачиваем в 3×2: колонка
    // становится ~190 px, а высота панели и так избыточна.
    //
    // Колоночный зазор 30 px оставлен для десктопа: в нём живут вертикальные
    // кнопки зума (.vp-zoom-spread), вынесенные за край окна. На телефоне
    // это 60 px из 390 — там кнопки и так лежат поверх, хватает 8.
    // Размеры берём у документа, а не у окна: медиазапросы CSS считают именно
    // по нему, а window.innerWidth/innerHeight на мобильных и в эмуляции
    // устройства отдают другое число — сетка тогда расходится с вёрсткой.
    const _docEl = typeof document !== 'undefined' ? document.documentElement : null;
    const _vw = _docEl?.clientWidth || (typeof window !== 'undefined' ? window.innerWidth : 0);
    const _vh = _docEl?.clientHeight || (typeof window !== 'undefined' ? window.innerHeight : 0);
    const _portrait = _vh > _vw;
    const _narrow = _vw > 0 && _vw <= 768;
    let _rows = config.rows, _cols = config.cols;
    if (layout === 'mpr6' && _portrait) { _rows = 3; _cols = 2; }

    const _colGap = _cols > 1 ? (_narrow ? 8 : 30) : 0;

    container.innerHTML = '';
    // [10.3.2] Раскладка — на самом контейнере, чтобы CSS мог отличить MPR и
    // MPR 6 от одиночного окна. Без этой метки правило «только в MPR» написать
    // нечем: раньше раскладка жила только в JS.
    container.dataset.layout = layout;
    // [10.3.3] Та же метка на body: значок выбранного инструмента висит
    // фиксированно и контейнеру не родня, а правило «сдвинуть его в MPR»
    // должно до него дотянуться. Через :has() на body не сработало — тихо, без
    // ошибки, я это увидел только замером; метка-атрибут надёжнее.
    try { document.body.dataset.layout = layout; } catch (e) { /* */ }
    container.style.display = 'grid';
    container.style.gridTemplateRows = `repeat(${_rows}, 1fr)`;
    container.style.gridTemplateColumns = `repeat(${_cols}, 1fr)`;
    container.style.gap = _colGap ? `2px ${_colGap}px` : '2px';
    try {
      const wrap = document.getElementById('viewport-wrapper');
      wrap?.setAttribute('data-cols', String(_cols));

      // [10.0.2] Место ползунка листания — промежуток между первой и второй
      // колонкой, тот самый, ради которого сетка и раздвинута.
      //
      // Раньше координата была вшита в CSS правилом [data-cols="2"] → left:50%.
      // Для трёх колонок правило не срабатывало вовсе, и ползунок уезжал к
      // правому краю экрана (запасное right:34px) — из выделенного ему места
      // в чужое. Считаем честно: ширина колонки (100% − (N−1)·G)/N плюс
      // половина зазора. Для двух колонок выражение даёт ровно 50%, то есть
      // прежнее поведение сохраняется байт в байт.
      if (wrap) {
        const x = sliderXExpr(_cols, _colGap);
        if (x) wrap.style.setProperty('--slider-x', x);
        else wrap.style.removeProperty('--slider-x');
      }
    } catch (e) { /* */ }
    if (!container.style.height && !container.offsetHeight) container.style.height = '100%';
  
    const viewportInputs = [];
    config.viewports.forEach(vpId => {
      const el = document.createElement('div');
      el.id = `viewport-${vpId}`;
      el.style.cssText = 'width:100%;height:100%;position:relative;background:#000;touch-action:none;user-select:none;-webkit-user-select:none;';
  
      const label = document.createElement('div');
      label.textContent = VP_TITLES[vpId] || vpId;
      label.className = 'vp-orientation-label';
      el.appendChild(label);
  
      // [10.0.3] Кнопки окна разведены по углам, а не выстроены в ряд внизу.
      //
      // Масштаб «−/+» стоит в нижних углах ОДИНАКОВО во всех окнах — и в
      // срезовых, и в объёмных. Поворот объёма (его нет у срезов) уходит в
      // правый верхний угол: левый верхний занят подписью окна.
      //
      // Прежний ряд с justify-content:space-between раскладывал четыре
      // кнопки объёмного окна равномерно по ширине, и две средние ложились
      // ровно на изображение.
      el.appendChild(_buildViewportControls(vpId));

      container.appendChild(el);
  
      let type = ViewportType.STACK, orientation;
      // [10.0.2] mpr6 — та же логика типов, что и mpr: объёмные окна,
      // нативная стопка, остальные ортогональные проекции
      if (isMprLayout(layout)) {
        if (vpId === 'view-3d' || vpId === 'volume3d') type = ViewportType.VOLUME_3D;
        else if (vpId === 'native') { type = ViewportType.STACK; }
        else { type = ViewportType.ORTHOGRAPHIC; orientation = { axial: OrientationAxis.AXIAL, sagittal: OrientationAxis.SAGITTAL, coronal: OrientationAxis.CORONAL }[vpId]; }
      }
      viewportInputs.push({ viewportId: vpId, type, element: el, defaultOptions: { orientation, background: [0, 0, 0] } });
      state.viewports.set(vpId, { element: el, type, orientation });
    });
  
    const appEl = document.getElementById('app');
    if (appEl && getComputedStyle(appEl).display === 'none') appEl.style.display = 'flex';
    void container.offsetHeight;
  
    re.setViewports(viewportInputs);
    await awaitableResize(re, container);
    _attachContextLossHandlers();
  
    const newVpIds = viewportInputs.map(v => v.viewportId);
    try { initTools(newVpIds, RENDERING_ENGINE_ID); activateDataTools(); } catch (e) { log.warn('[VP] Tool init:', e.message); }
    return newVpIds;
  } catch (e) { log.warn(`[viewport] setupViewports failed:`, e.message); throw e; }
}

// [RICH] Живой счётчик среза
let _sliceOverlayBound = false;
let _mprSourceSliceCount = null;
export function getMprSourceSliceCount() { return _mprSourceSliceCount; }
let _mprVolumeDir = null;
let _mprVolumeHalfExtent = null;
let _mprVolumeCenter = null;

let _mprAlignMode = 'acquired';
try { localStorage.removeItem('mpr.align'); localStorage.removeItem('mpr.align.v2'); } catch (e) { /* */ }
export function getMprAlignMode() { return _mprAlignMode; }

function _bindLiveSliceOverlay() {
  if (_sliceOverlayBound) return;
  const container = document.getElementById('viewport-container');
  if (!container) return;
  _sliceOverlayBound = true;

  let _lastVpId = null;
  const _rememberVp = (ev) => {
    try {
      const re0 = getRenderingEngine();
      for (const id of (getViewportIds() || [])) {
        const el = re0?.getViewport(id)?.element;
        if (el && (el === ev.target || el.contains(ev.target))) { _lastVpId = id; return; }
      }
    } catch (e) { /* */ }
  };
  container.addEventListener('wheel', _rememberVp, true);
  container.addEventListener('pointerdown', _rememberVp, true);
  const _allowUpdate = (vpId) =>
    window._mobileActiveTool === 'Crosshairs' || !_lastVpId || vpId === _lastVpId;

  const axisLabel = (n, v) => {
    try {
      const ax = [Math.abs(n[0]), Math.abs(n[1]), Math.abs(n[2])];
      const i = ax.indexOf(Math.max(ax[0], ax[1], ax[2]));
      const val = v[i];
      if (!Number.isFinite(val)) return '';
      return `${val >= 0 ? ['L', 'P', 'S'][i] : ['R', 'A', 'I'][i]}${Math.abs(val).toFixed(1)}`;
    } catch (e) { return ''; }
  };
  const badge = (vpEl, text) => {
    if (!vpEl || !text) return;
    let b = vpEl.querySelector(':scope > .vp-slice-badge');
    if (!b) { b = document.createElement('div'); b.className = 'vp-slice-badge'; vpEl.appendChild(b); }
    b.textContent = text;
  };
  const updGlobal = (idx, total, imNum, isReformat, mm) => {
    if (!Number.isFinite(idx)) return;
    const s = document.getElementById('overlay-slice');
    if (s) {
      const srcTxt = (isReformat && _mprSourceSliceCount) ? ` · серия ${_mprSourceSliceCount} срез.` : '';
      s.textContent = `${isReformat ? 'Реформат' : 'Срез'}: ${idx}${total ? ' / ' + total : ''}${srcTxt}${mm ? ' · ' + mm : ''}`;
    }
    const ex = document.getElementById('overlay-exseim');
    if (ex) {
      const imTxt = isReformat ? (mm || '—') : `${imNum ?? idx}${total ? '/' + total : ''}`;
      ex.textContent = `Ex:${ex.dataset.ex || '—'}  Se:${ex.dataset.se || '—'}  Im:${imTxt}`;
    }
  };

  const _lastIdx = new Map();
  const _changed = (vpId, idx) => {
    if (_lastIdx.get(vpId) === idx) return false;
    _lastIdx.set(vpId, idx);
    return true;
  };
  const _is3D = (vpId) => {
    try { return String(getRenderingEngine()?.getViewport(vpId)?.type || '').toLowerCase().includes('3d'); }
    catch (e) { return false; }
  };

  container.addEventListener(cornerstone.Enums.Events.STACK_NEW_IMAGE, (ev) => {
    const d = ev.detail || {};
    const idx = (d.imageIdIndex ?? 0) + 1;
    if (!_allowUpdate(d.viewportId)) return;
    if (!_changed(d.viewportId, idx)) return;
    let total, im, mm = '';
    try { total = getRenderingEngine()?.getViewport(d.viewportId)?.getImageIds?.()?.length; } catch (e) { total = undefined; }
    try {
      const nat = cornerstone.metaData.get('instance', d.imageId);
      im = parseInt(Array.isArray(nat?.InstanceNumber) ? nat.InstanceNumber[0] : nat?.InstanceNumber, 10);
    } catch (e) { im = undefined; }
    if (!Number.isFinite(im)) {
      try { im = parseInt(cornerstone.metaData.get('generalImageModule', d.imageId)?.instanceNumber, 10); } catch (e) { im = undefined; }
    }
    try {
      const pl = cornerstone.metaData.get('imagePlaneModule', d.imageId);
      const ipp = pl?.imagePositionPatient, rc = pl?.rowCosines, cc = pl?.columnCosines;
      if (ipp && rc && cc) {
        const n = [rc[1] * cc[2] - rc[2] * cc[1], rc[2] * cc[0] - rc[0] * cc[2], rc[0] * cc[1] - rc[1] * cc[0]];
        mm = axisLabel(n, ipp);
      }
    } catch (e) { mm = ''; }
    const line = `Срез: ${idx}${total ? '/' + total : ''} · Im:${Number.isFinite(im) ? im : idx}${mm ? ' · ' + mm : ''}`;
    badge(ev.target, line);
    if (ev.target?.matches?.(':hover')) updGlobal(idx, total, Number.isFinite(im) ? im : null, false, mm);
  }, true);

  container.addEventListener(cornerstone.Enums.Events.VOLUME_NEW_IMAGE, (ev) => {
    const d = ev.detail || {};
    const idx = (d.imageIndex ?? d.sliceIndex ?? 0) + 1;
    if (_is3D(d.viewportId)) return;
    if (!_allowUpdate(d.viewportId)) return;
    if (!_changed(d.viewportId, idx)) return;
    let mm = '';
    try {
      const cam = getRenderingEngine()?.getViewport(d.viewportId)?.getCamera?.();
      if (cam?.focalPoint && cam?.viewPlaneNormal) mm = axisLabel(cam.viewPlaneNormal, cam.focalPoint);
    } catch (e) { mm = ''; }
    const src = _mprSourceSliceCount ? ` · серия ${_mprSourceSliceCount} срез.` : '';
    badge(ev.target, `Реформат: ${idx}/${d.numberOfSlices ?? '?'}${src}${mm ? ' · ' + mm : ''}`);
    if (ev.target?.matches?.(':hover')) updGlobal(idx, d.numberOfSlices, null, true, mm);
  }, true);
}

// [MR-3D] Диапазон ткани последнего МР-объёма
let _mrTissueRange = null;

/**
 * [10.19.14] Перенос для МРТ по гистограмме ЭТОЙ серии.
 *
 * ПОВОД. Список «Перенос» в панели рендеринга состоял из трёх пунктов, и все три
 * — КТ. У МРТ нет единиц Хаунсфилда: яркость произвольная и зависит от аппарата,
 * катушки, последовательности и усиления приёмника. Один и тот же перенос на двух
 * МР-исследованиях даёт разную картинку, а на третьем — пустой экран. Поэтому
 * окно берётся не из таблицы, а считается по гистограмме конкретной серии
 * (core/mrNormalize.js), и форма кривой задаётся в ДОЛЯХ, которые переводятся в
 * яркость этой серии.
 *
 * Отказ вместо тихой лжи: если гистограмма одномодальная (в кадре только шум,
 * пустой объём, однородный фантом) — окно не строится и возвращается причина.
 *
 * @param {string} идПресета 'mr-brain' | 'mr-vessels' | 'mr-overview'
 * @returns {{ok: boolean, причина?: string, окно?: object, точек?: number, модальность?: string}}
 */
export function применитьМРПеренос(идПресета) {
  try {
    const re = getRenderingEngine();
    const vpId = (getViewportIds() || []).find((id) => String(re?.getViewport(id)?.type || '').toLowerCase().includes('3d'));
    const vp = vpId ? re.getViewport(vpId) : null;
    if (!vp) return { ok: false, причина: 'в раскладке нет объёмного окна' };

    const данные = _скалярыАктивногоОбъёма();
    if (!данные?.length) return { ok: false, причина: 'объём не загружен' };

    const окно = окноСерииМР(данные, { шаг: данные.length > 4e6 ? Math.ceil(данные.length / 4e6) : 1 });
    if (!окно.надёжно) return { ok: false, причина: окно.причина, окно };

    const перенос = переносМРДляСерии(идПресета, окно);
    if (!перенос) return { ok: false, причина: `неизвестный перенос «${идПресета}»`, окно };

    const prop = vp.getDefaultActor?.()?.actor?.getProperty?.();
    if (!prop) return { ok: false, причина: 'у объёмного окна нет свойств актёра', окно };

    const o = prop.getScalarOpacity(0);
    o.removeAllPoints();
    for (const [яркость, непрозрачность] of перенос.точки) o.addPoint(яркость, непрозрачность);
    try { prop.getRGBTransferFunction(0).setMappingRange(окно.низ, окно.верх); } catch (e) { log.debug('[МР-перенос/цвет]', e.message); }
    try { vp.setProperties?.({ voiRange: { lower: окно.низ, upper: окно.верх } }); } catch (e) { log.debug('[МР-перенос/voi]', e.message); }
    _mrTissueRange = { lo: окно.низ, hi: окно.верх };
    vp.render?.();
    state.cinematicRenderer?.forceRender?.();

    log.info(`[МР-перенос] ${перенос.имя}: окно ${Math.round(окно.низ)}…${Math.round(окно.верх)} (ww ${Math.round(окно.ww)}), фон отсечён на ${Math.round(окно.порогФона)}`);
    return { ok: true, окно, точек: перенос.точки.length, модальность: _volumeModality };
  } catch (e) {
    return { ok: false, причина: e.message };
  }
}

/** Скаляры активного объёма — тремя путями, как в остальном коде CS3D v2. */
function _скалярыАктивногоОбъёма() {
  try {
    const id = knownVolumeIds?.()?.[0] || null;
    const volume = id ? cornerstone.cache.getVolume(id) : null;
    if (!volume) return null;
    let sd = null;
    try { sd = volume.voxelManager?.getCompleteScalarDataArray?.(); } catch (e) { /* */ }
    if (!sd && volume.imageData) try { sd = volume.imageData.getPointData?.()?.getScalars?.()?.getData?.(); } catch (e) { /* */ }
    if (!sd) try { sd = volume.getScalarData?.(); } catch (e) { /* */ }
    return sd || null;
  } catch (e) { return null; }
}

export function applyMR3DPreset(kind) {
  try {
    const re = getRenderingEngine();
    const vpId = (getViewportIds() || []).find((id) => String(re?.getViewport(id)?.type || '').toLowerCase().includes('3d'));
    const vp = vpId ? re.getViewport(vpId) : null;
    if (!vp) { showToast('3D-окно не найдено в текущей раскладке', 'warning', 2200); return false; }
    if (!_mrTissueRange) { showToast('Сначала загрузите МР-объём', 'warning', 2200); return false; }
    const { lo, hi } = _mrTissueRange;
    const prop = vp.getDefaultActor?.()?.actor?.getProperty?.();
    if (!prop) return false;
    const o = prop.getScalarOpacity(0);
    const c = prop.getRGBTransferFunction(0);
    const B = cornerstone.Enums.BlendModes;
    o.removeAllPoints();
    if (kind === 'mip') {
      vp.setBlendMode?.(B.MAXIMUM_INTENSITY_BLEND);
      o.addPoint(lo, 0.0); o.addPoint(hi, 1.0);
      c.setMappingRange(lo, hi);
    } else if (kind === 'skin') {
      vp.setBlendMode?.(B.COMPOSITE);
      o.addPoint(lo * 0.5, 0.0); o.addPoint(lo, 0.9); o.addPoint(hi, 0.95);
      c.setMappingRange(lo * 0.5, hi);
    } else {
      vp.setBlendMode?.(B.COMPOSITE);
      o.addPoint(lo, 0.0); o.addPoint(lo + (hi - lo) * 0.2, 0.03);
      o.addPoint(hi * 0.8, 0.3); o.addPoint(hi, 0.4);
      c.setMappingRange(lo, hi);
    }
    vp.render?.();
    return true;
  } catch (e) { log.warn('[MR3D]', e.message); return false; }
}

export function updateNativeLabel(imageIds) {
  try {
    const el = document.querySelector('#viewport-native .vp-orientation-label');
    if (!el || !imageIds?.length) return;
    const pl = cornerstone.metaData.get('imagePlaneModule', imageIds[Math.floor(imageIds.length / 2)]);
    const rc = pl?.rowCosines, cc = pl?.columnCosines;
    let plane = '';
    if (rc?.length === 3 && cc?.length === 3) {
      const n = [
        Math.abs(rc[1] * cc[2] - rc[2] * cc[1]),
        Math.abs(rc[2] * cc[0] - rc[0] * cc[2]),
        Math.abs(rc[0] * cc[1] - rc[1] * cc[0]),
      ];
      const i = n.indexOf(Math.max(n[0], n[1], n[2]));
      plane = ['Sagittal', 'Coronal', 'Axial'][i] || '';
    }
    el.style.textTransform = 'none';
    el.textContent = plane ? `${plane} · нативная серия` : 'Нативные срезы';
  } catch (e) { log.debug('[NATIVE] label:', e.message); }
}

const _FRAME_FILL = 0.92;
function _fitToFrame(vp) {
  try {
    if (!vp || String(vp.type || '').toLowerCase().includes('3d')) return;
    vp.resetCamera?.();
    const cam = vp.getCamera?.();
    if (!cam || !Number.isFinite(cam.parallelScale)) return;
    vp.setCamera({ parallelScale: cam.parallelScale / _FRAME_FILL });
  } catch (e) { log.debug('[fitToFrame]', e.message); }
}

/** Применить текущий режим выравнивания к MPR-окнам */
export function applyMprAlignment(volumeDir) {
  try {
    const re = getRenderingEngine();
    if (!re) return 0;
    const ids = getViewportIds() || [];
    let dir = volumeDir || _mprVolumeDir;
    if (!dir) {
      for (const id of ids) {
        const vp = re.getViewport(id);
        try {
          const vol = vp?.getActors?.()?.[0] && cornerstone.cache.getVolume?.(vp.getActors()[0].uid);
          if (vol?.direction?.length === 9) { dir = vol.direction; break; }
        } catch (e) { /* */ }
      }
    }
    if (dir?.length === 9) _mprVolumeDir = dir;
    if (!dir || dir.length !== 9) return 0;
    const I = [dir[0], dir[1], dir[2]], J = [dir[3], dir[4], dir[5]], K = [dir[6], dir[7], dir[8]];
    const tiltCos = Math.max(Math.abs(K[0]), Math.abs(K[1]), Math.abs(K[2]));
    const tiltDeg = Math.acos(Math.min(1, tiltCos)) * 180 / Math.PI;
    const MPRV = cornerstone.CONSTANTS?.MPR_CAMERA_VALUES;
    if (!MPRV) return tiltDeg;

    const mul = (_mprAlignMode === 'body')
      ? ((v) => [
          v[0] * I[0] + v[1] * J[0] + v[2] * K[0],
          v[0] * I[1] + v[1] * J[1] + v[2] * K[1],
          v[0] * I[2] + v[1] * J[2] + v[2] * K[2],
        ])
      : ((v) => v);
    let _diag = [];
    for (const _vpO of ids) {
      const key = String(_vpO).toLowerCase();
      const std = MPRV[key];
      const vpO = re.getViewport(_vpO);
      if (!std || !vpO || String(vpO.type || '').toLowerCase().includes('3d')) continue;
      try {
        const want = mul(std.viewPlaneNormal);
        vpO.setOrientation(
          { viewPlaneNormal: want, viewUp: mul(std.viewUp) },
          false
        );
        try {
          const cam = vpO.getCamera?.();
          if (cam && Number.isFinite(cam.parallelScale)) {
            const halfMm = Number.isFinite(_mprVolumeHalfExtent) && _mprVolumeHalfExtent > 0
              ? _mprVolumeHalfExtent : cam.parallelScale;
            const patch = { parallelScale: halfMm / _FRAME_FILL };
            if (Array.isArray(_mprVolumeCenter)) {
              const n = want;
              const dist = halfMm * 4;
              patch.focalPoint = [..._mprVolumeCenter];
              patch.position = [
                _mprVolumeCenter[0] + n[0] * dist,
                _mprVolumeCenter[1] + n[1] * dist,
                _mprVolumeCenter[2] + n[2] * dist,
              ];
              patch.viewUp = mul(std.viewUp);
            }
            vpO.setCamera(patch);
          }
        } catch (e) { log.debug('[MPR fit]', e.message); }
        vpO.render?.();
        const got = vpO.getCamera?.()?.viewPlaneNormal;
        if (got) _diag.push(`${key}:[${got.map(x => x.toFixed(2)).join(',')}]`);
      } catch (e) { log.debug('[MPR align vp]', _vpO, e.message); }
    }
    log.info(`[MPR align] режим=${_mprAlignMode} наклон=${tiltDeg.toFixed(1)}° центр=${Array.isArray(_mprVolumeCenter) ? '[' + _mprVolumeCenter.map(x => x.toFixed(0)).join(',') + ']' : 'НЕТ'} → ${_diag.join(' ')}`);

    try {
      const stateTxt = (tiltDeg > 2)
        ? (_mprAlignMode === 'body' ? 'выпрямлено по телу' : `как снято (наклон ${tiltDeg.toFixed(0)}°)`)
        : '';
      for (const _vpS of ids) {
        const vpEl = re.getViewport(_vpS)?.element;
        if (!vpEl || String(re.getViewport(_vpS)?.type || '').toLowerCase().includes('3d')) continue;
        let tag = vpEl.querySelector(':scope > .vp-align-state');
        if (!tag) {
          tag = document.createElement('div');
          tag.className = 'vp-align-state';
          vpEl.appendChild(tag);
        }
        tag.textContent = stateTxt;
        tag.style.display = stateTxt ? 'block' : 'none';
      }
    } catch (e) { log.debug('[MPR state label]', e.message); }
    try {
      const vp3 = ids.map((id) => re.getViewport(id)).find((v) => String(v?.type || '').toLowerCase().includes('3d'));
      if (vp3) {
        if (_mprAlignMode === 'body') vp3.setCamera({ viewPlaneNormal: [0, -1, 0], viewUp: [0, 0, 1] });
        else vp3.setCamera({ viewPlaneNormal: [-J[0], -J[1], -J[2]], viewUp: [K[0], K[1], K[2]] });
        vp3.resetCamera?.(); vp3.render?.();
      }
    } catch (e) { log.debug('[MPR/3D align]', e.message); }
    return tiltDeg;
  } catch (e) { log.warn('[MPR] applyMprAlignment:', e.message); return 0; }
}

export function centerTissueVertically(delayMs = 260) {
  setTimeout(() => {
    try {
      const re = getRenderingEngine();
      if (!re) return;
      for (const id of (getViewportIds() || [])) {
        const vp = re.getViewport(id);
        if (!vp || String(vp.type || '').toLowerCase().includes('3d')) continue;
        const canvas = vp.canvas || vp.element?.querySelector('canvas');
        if (!canvas || !canvas.width || !canvas.height) continue;
        const W = canvas.width, H = canvas.height;
        let data;
        try {
          const tmp = document.createElement('canvas');
          tmp.width = W; tmp.height = H;
          const tctx = tmp.getContext('2d');
          if (!tctx) continue;
          tctx.drawImage(canvas, 0, 0);
          data = tctx.getImageData(0, 0, W, H).data;
        } catch (e) { continue; }
        const THRESH = 18;
        let top = -1, bottom = -1;
        for (let y = 0; y < H; y++) {
          let bright = 0;
          const row = y * W * 4;
          for (let x = 0; x < W; x += 4) {
            const i = row + x * 4;
            if (data[i] > THRESH || data[i + 1] > THRESH || data[i + 2] > THRESH) { bright++; if (bright > 3) break; }
          }
          if (bright > 3) { if (top < 0) top = y; bottom = y; }
        }
        if (top < 0 || bottom < 0 || bottom <= top) continue;
        const tissueMidY = (top + bottom) / 2;
        const windowMidY = H / 2;
        const offsetPx = tissueMidY - windowMidY;
        if (Math.abs(offsetPx) < 3) continue;
        const cam = vp.getCamera?.();
        if (!cam || !Number.isFinite(cam.parallelScale) || !Array.isArray(cam.focalPoint) || !Array.isArray(cam.viewUp)) continue;
        const mmPerPx = (2 * cam.parallelScale) / H;
        const shiftMm = offsetPx * mmPerPx;
        const up = cam.viewUp;
        const nf = [
          cam.focalPoint[0] + up[0] * shiftMm,
          cam.focalPoint[1] + up[1] * shiftMm,
          cam.focalPoint[2] + up[2] * shiftMm,
        ];
        const dPos = [
          cam.position[0] + up[0] * shiftMm,
          cam.position[1] + up[1] * shiftMm,
          cam.position[2] + up[2] * shiftMm,
        ];
        vp.setCamera({ focalPoint: nf, position: dPos });
        vp.render?.();
      }
    } catch (e) { log.debug('[centerTissue]', e.message); }
  }, delayMs);
}

export function toggleMprAlignMode() {
  _mprAlignMode = (_mprAlignMode === 'body') ? 'acquired' : 'body';
  const tilt = applyMprAlignment();
  return { mode: _mprAlignMode, tilt };
}
export async function loadStack(viewportId, imageIds) {
  try {
    const re = getRenderingEngine();
    const vp = re.getViewport(viewportId);
    if (!vp) throw new Error(`Viewport ${viewportId} not found`);
    const el = vp.element;
    if (el?.offsetWidth === 0 || el?.offsetHeight === 0) {
      await Promise.race([
        new Promise(r => { const ro = new ResizeObserver(entries => { for (const e of entries) if (e.contentRect.width > 0) { ro.disconnect(); re.resize(true, true); r(); } }); ro.observe(el); }),
        new Promise(r => setTimeout(() => { re.resize(true, true); r(); }, 500)),
      ]);
    }
    await vp.setStack(imageIds);
    if (imageIds?.length) { const id0 = imageIds[vp.getCurrentImageIdIndex?.() || 0]; if (id0) memoryGuardian.touch(id0); }
    requestAnimationFrame(() => { try { re.resize(true, true); vp.render(); } catch (e) { log.debug("[viewport]", e.message); } });
    vp.render();
    requestAnimationFrame(() => {
      try {
        _fitToFrame(vp);
        vp.render();
      } catch (e) { log.debug('[viewport] поля стопки:', e.message); }
    });
    events.emit(EVENTS.STACK_LOADED, { viewportId, imageIds, count: imageIds?.length || 0 });
    return vp;
  } catch (e) { log.warn(`[viewport] loadStack failed:`, e.message); throw e; }
}

// [9.0.0] setWindowLevel переехала в viewportState.js — там она разрывает
// круг viewport.js ↔ viewportEffects.js. Здесь остаётся только вывеска.
export { setWindowLevel };

export function resetViewport(viewportId) {
  const vp = getRenderingEngine().getViewport(viewportId);
  if (!vp) return;
  vp.resetCamera(); vp.resetProperties(); vp.render();
}

export function zoomViewport(viewportId, direction) {
  const re = getRenderingEngine();
  const vp = re?.getViewport(viewportId);
  if (!vp) return;
  const camera = vp.getCamera();
  const factor = direction === 'in' ? 1 / 1.2 : 1.2;
  if (camera.parallelProjection) {
    vp.setCamera({ parallelScale: camera.parallelScale * factor });
  } else {
    const pos = camera.position, fp = camera.focalPoint;
    const dist = Math.sqrt(pos.reduce((s, v, i) => s + (v - fp[i]) ** 2, 0));
    const newDist = dist * factor;
    if (direction === 'in' && newDist < 10) return;
    const dir = pos.map((v, i) => (v - fp[i]) / dist);
    vp.setCamera({ position: fp.map((f, i) => f + dir[i] * newDist) });
  }
  const allIds = getViewportIds();
  allIds.length > 1 ? re.renderViewports(allIds) : vp.render();
}

export function set3DPreset(presetName, viewportId) {
  if (!presetName || presetName === 'null' || presetName === 'undefined') return false;
  const re = getRenderingEngine();
  const vpId = viewportId || find3DViewportId();
  let vp3d = re?.getViewport(vpId);
  if (!vp3d) { for (const [id] of state.viewports) { const vp = re.getViewport(id); if (vp?.type === ViewportType.VOLUME_3D) { vp3d = vp; break; } } }
  if (!vp3d) return false;
  const ok = apply3DPreset(vp3d, presetName);
  if (ok) { state.cinematicRenderer?.forceRender(); return true; }
  try { vp3d.setProperties({ preset: presetName }); vp3d.render(); state.cinematicRenderer?.forceRender(); return true; } catch { return false; }
}

/**
 * [10.0.2] Применить пресет объёмной отрисовки — в ОДНО окно, а не во все.
 *
 * Само правило «кому адресован пресет» живёт в presetPlan.js отдельной чистой
 * функцией и покрыто проверками; здесь только исполнение плана.
 *
 * @param {string} presetName идентификатор пресета из presets3d
 * @param {{ww:number, wc:number}} [wwwcMap] сопутствующее окно плотности
 * @returns {{target: string|null, applied: boolean, fallbackWL: boolean, reason: string}}
 */
/**
 * [10.0.3] Подготовить пресет под шкалу текущей серии.
 *
 * Возвращает идентификатор, который и надо применять: либо исходный, либо
 * временный — с точками, перенесёнными на диапазон данных.
 *
 * Перенос делается ТОЛЬКО для серий без шкалы HU (МР, УЗИ и прочее). У КТ
 * числа означают ткань — жир −100, мышца +40, кость +700, — и растягивать их
 * под гистограмму значит врать о плотности. Там, где пресет не попал в данные
 * и подвинуть его нельзя, честно пишем об этом в поле notice.
 *
 * @param {string} presetName
 * @param {object} result объект результата, куда кладём пояснение
 * @returns {string} идентификатор пресета для применения
 */
function _prepareRangedPreset(presetName, result) {
  try {
    const preset = getPreset3D?.(presetName);
    if (!preset) return presetName;

    const plan = planPresetRange({
      preset,
      modality: _volumeModality,
      dataRange: _volumeValueRange,
    });

    if (plan.action === 'as-is') return presetName;

    if (plan.action === 'empty') {
      result.notice = `Пресет «${preset.name || presetName}» рассчитан на другую шкалу: ${plan.reason}`;
      log.warn(`[preset] ${presetName}: ${plan.reason}`);
      return presetName;   // применим как есть — пусть врач видит результат сам
    }

    const moved = rescalePreset(preset, plan.support, plan.target);
    if (!moved) return presetName;

    const tmpKey = `_ranged-${presetName}`;
    const added = addCustomPreset?.(tmpKey, moved);
    if (added && added.success === false) return presetName;

    result.rescaled = true;
    result.notice = `Пресет «${preset.name || presetName}» подогнан под шкалу серии (${plan.reason})`;
    log.info(`[preset] ${presetName} → ${tmpKey}: ${plan.reason}`);
    return tmpKey;
  } catch (e) {
    log.debug('[preset] подготовка шкалы:', e.message);
    return presetName;
  }
}

export function applyRenderPreset(presetName, wwwcMap) {
  const re = getRenderingEngine();
  const result = { target: null, applied: false, fallbackWL: false, reason: '' };
  if (!re) return result;

  const viewports = [];
  for (const [vpId] of state.viewports) {
    const vp = re.getViewport(vpId);
    if (!vp) continue;
    viewports.push({ id: vpId, is3D: vp.type === ViewportType.VOLUME_3D });
  }

  const plan = planPresetApply({ viewports, presetName, wwwc: wwwcMap });
  result.reason = plan.reason;

  if (plan.target) {
    const vp = re.getViewport(plan.target);
    if (vp) {
      result.target = plan.target;

      // [10.0.3] Перед применением — сверить шкалу пресета со шкалой серии.
      //
      // «Кожа» и «Мышцы» описаны в HU: прозрачность у них не равна нулю
      // только в −200…+200 и −80…+500 соответственно. На МР этих чисел нет
      // вовсе (там своя условная шкала, обычно 0…1500), и обе кривые
      // оказывались мимо всех данных: пресет применялся без ошибки, в
      // журнале стояло «✅ Preset applied», а окно чернело. Снаружи это
      // ровно то, что называют «пресет не работает».
      const key = _prepareRangedPreset(presetName, result);

      result.applied = !!apply3DPreset(vp, key);
      if (!result.applied) {
        try { vp.setProperties({ preset: key }); result.applied = true; }
        catch (e) { log.debug('[preset]', e.message); }
      }
      try { vp.render(); } catch (e) { /* */ }
      try { state.cinematicRenderer?.forceRender(); } catch (e) { /* */ }
    }
    return result;
  }

  // [10.3.0] Без окна яркости дальше идти нельзя.
  //
  // Здесь читалось wwwcMap.ww без проверки, а wwwcMap бывает null — например
  // у киносъёмки с ангиографа, где в тегах нет ни WindowCenter, ни
  // WindowWidth. Выходило «Cannot read properties of null (reading 'ww')»
  // ровно в момент открытия серии: снимок уже загружен, а на экране ошибка.
  if (!wwwcMap || wwwcMap.ww === undefined || wwwcMap.wc === undefined) {
    if (plan.wlTargets?.length) log.debug('[preset/WL] окно яркости не задано тегами — оставляю как есть');
    return result;
  }

  for (const vpId of plan.wlTargets) {
    const vp = re.getViewport(vpId);
    if (!vp) continue;
    try {
      const { lower, upper } = cornerstone.utilities.windowLevel.toLowHighRange(wwwcMap.ww, wwwcMap.wc);
      vp.setProperties({ voiRange: { lower, upper } });
      vp.render();
      result.fallbackWL = true;
    } catch (e) { log.debug('[preset/WL]', e.message); }
  }
  return result;
}

/**
 * Окно плотности на срезовые окна — самостоятельное действие.
 *
 * Нужно там, где ww/wc это и есть содержание пресета (пользовательские
 * пресеты окна), а не довесок к объёмному.
 */
export function applyWindowToSlices(wwwcMap) {
  const re = getRenderingEngine();
  if (!re || wwwcMap?.ww === undefined || wwwcMap?.wc === undefined) return 0;
  let n = 0;
  for (const [vpId] of state.viewports) {
    const vp = re.getViewport(vpId);
    if (!vp || vp.type === ViewportType.VOLUME_3D) continue;
    try {
      const { lower, upper } = cornerstone.utilities.windowLevel.toLowHighRange(wwwcMap.ww, wwwcMap.wc);
      vp.setProperties({ voiRange: { lower, upper } });
      vp.render();
      n++;
    } catch (e) { log.debug('[WL/срезы]', e.message); }
  }
  return n;
}

/**
 * Старое имя. Оставлено ради внешних вызовов (панель enterprise, голосовые
 * команды, пользовательские скрипты), но ведёт себя по новому правилу: пресет
 * — в своё окно. Разное поведение у двух имён было бы хуже любого из них.
 */
export function applyPresetToAll(presetName, wwwcMap) {
  return applyRenderPreset(presetName, wwwcMap);
}

// Cinematic API
export function getCinematicRendererInstance() { return getCinematicRenderer(); }

/**
 * [10.19.12] Рендерер по требованию — с объяснением, если его быть не может.
 *
 * ПОВОД. Кинематографический рендерер создаётся только при создании объёмного
 * окна (`_initCinematic` из раскладки). Кнопка «Рендер» в панели про это не
 * знала: она брала `getCinematicRenderer()`, получала null в любой раскладке без
 * 3D и уходила в `cinematicToggle`, который писал в консоль
 * «[Cinematic] Не инициализирован» — и всё. Ни плашки, ни движения полосы
 * прогресса: снаружи кнопка просто не работала, и понять почему было нельзя.
 *
 * Здесь причина возвращается вызывающему, чтобы он сказал её человеку:
 *   { рендерер, причина: null }            — есть, можно работать
 *   { рендерер: null, причина: 'нет-3d' }  — в раскладке нет объёмного окна
 *   { рендерер: null, причина: <текст> }   — окно есть, создание не удалось
 */
export function ensureCinematicRenderer() {
  const готовый = getCinematicRenderer();
  if (готовый && !готовый.isDisposed?.()) return { рендерер: готовый, причина: null };

  const re = getRenderingEngine();
  if (!re) return { рендерер: null, причина: 'нет-движка' };

  let vp3d = null;
  try { vp3d = re.getViewport(find3DViewportId()); } catch (e) { log.debug('[Cinematic]', e.message); }
  if (!vp3d) return { рендерер: null, причина: 'нет-3d' };

  _initCinematic(vp3d);
  const созданный = getCinematicRenderer();
  if (созданный) return { рендерер: созданный, причина: null };
  return { рендерер: null, причина: 'создание не удалось — смотрите журнал [Cinematic]' };
}

export function toggleCinematic() {
  const cr = getCinematicRenderer();
  if (!cr) { log.warn('[Cinematic] Не инициализирован'); return false; }
  cr.isEnabled() ? cr.disable() : cr.enable();
  return cr.isEnabled();
}
export function setCinematicPreset(presetId) { getCinematicRenderer()?.setPreset(presetId); }
export function mountCinematicPanel(containerId) {
  if (_ac) _ac.abort();
  _ac = new AbortController();
  const cr = getCinematicRenderer();
  if (!cr) return null;
  const el = document.getElementById(containerId);
  return el ? createCinematicPanel(cr, el) : null;
}

export function purgeCornerstoneCache() {
  try { noteMemoryEvent('purgeCornerstoneCache', 'очистил весь кэш', `томов в реестре было: ${volumeCount()}, кадров под защитой: ${allImageIds().length}`); } catch (e) { /* */ }
  forgetAllVolumes();
  try { cornerstone.cache.purgeCache?.(); } catch (e) { log.debug("[viewport]", e.message); }
}

export function destroy() {
  _cancelCamRAF();
  if (_histogramAC) { _histogramAC.abort(); _histogramAC = null; }
  _detachContextLossHandlers();
  if (state.cinematicRenderer) { try { detachDepthExtractor(); } catch (e) { log.debug("[viewport]", e.message); } try { state.cinematicRenderer.dispose(); } catch { /* */ } setCinematicRenderer(null); }
  if (state.renderingEngine) { state.renderingEngine.destroy(); state.renderingEngine = null; }
  state.viewports.clear();
  state.volumeAbortController = null;
  purgeCornerstoneCache();
}

// ══════════════════════════════════════════════════════════════
//  GLOBAL HANDLERS (resize, visibility, camera sync)
// ══════════════════════════════════════════════════════════════

/**
 * [10.1.6] Пересчёт после смены ШИРИНЫ окон (открылась или закрылась панель).
 *
 * Отличается от обычного resize одним, но решающим: здесь keepCamera = false.
 *
 * Обработчик события resize ниже сохраняет камеру нарочно — человек тянет
 * край окна, и сбрасывать его увеличение при этом нельзя. Но когда окно
 * ужимается из-за открытой панели, сохранённая камера посчитана для ПРЕЖНЕЙ,
 * широкой рамки: канва ужимается, а изображение остаётся в старом масштабе и
 * вылезает за край. Снаружи это «панель отрезала правую часть снимка».
 *
 * Поэтому здесь вписываем заново. Увеличение теряется — это осознанная цена:
 * потерять масштаб неприятно, потерять кусок снимка, по которому ставят
 * диагноз, недопустимо.
 */
export function relayoutViewports() {
  const re = getRenderingEngine();
  if (!re) return false;
  try {
    re.resize(true, false);
    const ids = getViewportIds();
    for (const id of ids) {
      try {
        const vp = re.getViewport(id);
        if (!vp) continue;
        if (vp.type === ViewportType.VOLUME_3D) _fit3DCamera(vp);
        else if (typeof vp.resetCamera === 'function') vp.resetCamera();
      } catch (e) { log.debug('[relayout]', id, e.message); }
    }
    if (ids.length) re.renderViewports(ids);
    return true;
  } catch (e) { log.warn('[relayout]', e.message); return false; }
}

let _resizeTimer = null;
function _onResize() {
  clearTimeout(_resizeTimer);
  _resizeTimer = setTimeout(() => {
    const re = getRenderingEngine();
    if (re) { try { re.resize(true, true); const ids = getViewportIds(); if (ids.length) re.renderViewports(ids); } catch (e) { log.debug("[viewport]", e.message); } }
  }, 250);
}

window.addEventListener('resize', _onResize, { passive: true, signal: state.globalAC.signal });
window.addEventListener('orientationchange', () => setTimeout(_onResize, 400), { passive: true, signal: state.globalAC.signal });

// ══════════════════════════════════════════════════════════════
//  RECOVERY HELPERS
// ══════════════════════════════════════════════════════════════

function _volumeById(volId) {
  const fullId = String(volId).startsWith('cornerstoneStreamingImageVolume:')
    ? volId : `cornerstoneStreamingImageVolume:${volId}`;
  try { return cornerstone.cache.getVolume?.(volId) || cornerstone.cache.getVolume?.(fullId) || null; }
  catch (e) { return null; }
}

/**
 * Тома, показанные сейчас во вьюпортах.
 *
 * [9.0.4] ГЛАВНАЯ ПОПРАВКА. Раньше сюда попадал КАЖДЫЙ актёр вьюпорта, а его
 * uid принимался за идентификатор тома. Но актёром бывает не только том:
 * наложение сегментации, контуры RTSTRUCT, картинка стопки — у всех у них uid
 * это случайный UUID вроде 60dc31fd-f355-451a-ad23-3b6c513931c6.
 *
 * Дальше происходило вот что. Такой UUID в кэше томов не находился — значит
 * «том пропал». Записи о нём в учёте, разумеется, не было — значит «нечем
 * восстановить». И так на каждое сворачивание окна: три несуществующих тома,
 * тревога, плашка на весь экран. Настоящие тома при этом были целы; в отчёте
 * они и стоят рядом, под своими именами volume_1.2.840…
 *
 * Отличить одно от другого просто: настоящий том либо лежит в кэше, либо
 * известен учёту (тогда он и правда пропал и его можно вернуть). Случайный
 * UUID актёра не бывает ни там, ни там.
 */
/**
 * [10.8.0] Тома, которые надо считать показанными, даже если движок их не
 * называет.
 *
 * Страж памяти сбрасывает всё, чего не видит на экране. Видит он по именам
 * актёров окна — а слой наложения этот движок именует случайным набором букв,
 * своё имя ему задать не удаётся. Выходило так: совмещение положило слой, через
 * триста миллисекунд страж его не узнал и выбросил том, и наложение исчезало
 * само собой. В журнале при этом стояло «наложение включено», а диапазон
 * значений слоя со второго раза шёл [0, 1] — верный признак, что тома уже нет.
 *
 * Поэтому тот, кто держит слой, говорит об этом прямо.
 */
const _считатьПоказанными = new Set();

export function keepVolumeShown(id) {
  if (id) _считатьПоказанными.add(canonicalId(id));
}
export function releaseVolumeShown(id) {
  if (id) _считатьПоказанными.delete(canonicalId(id));
}

function _visibleVolumeIds() {
  const ids = new Set();
  try {
    for (const id of _считатьПоказанными) ids.add(id);
    const re = getRenderingEngine();
    const known = new Set(knownVolumeIds());
    for (const vpId of getViewportIds()) {
      const vp = re?.getViewport(vpId);
      if (!vp || vp.type === ViewportType.STACK) continue;
      for (const a of (vp.getActors?.() || [])) {
        const raw = a?.uid || a?.referencedId || a?.referenceId || a?.volumeId;
        if (!raw) continue;
        const id = canonicalId(raw);
        // Это том, если он есть в кэше или числится в учёте. Иначе — актёр
        // сегментации, контура или картинки: к восстановлению отношения нет.
        if (_volumeById(id) || known.has(id)) ids.add(id);
      }
    }
  } catch (e) { log.debug('[recover] список томов:', e.message); }
  return [...ids];
}

// [v7.0.61] Защита от setProperties(undefined)
function _snapshotViewports() {
  const snap = [];
  try {
    const re = getRenderingEngine();
    for (const vpId of getViewportIds()) {
      const vp = re?.getViewport(vpId);
      if (!vp || vp.type === ViewportType.STACK) continue;
      let camera = null, props = null;
      try { camera = vp.getCamera?.(); } catch (e) { /* */ }
      try {
        const p = vp.getProperties?.();
        if (p && typeof p === 'object' && Object.keys(p).length > 0) props = p;
      } catch (e) { /* */ }
      snap.push({ vpId, camera, props });
    }
  } catch (e) { log.debug('[recover] снимок:', e.message); }
  return snap;
}

function _restoreViewports(snap) {
  const re = getRenderingEngine();
  for (const s of (snap || [])) {
    try {
      const vp = re?.getViewport(s.vpId);
      if (!vp) continue;
      if (s.props) { try { vp.setProperties?.(s.props); } catch (e) { /* */ } }
      if (s.camera) { try { vp.setCamera(s.camera); } catch (e) { /* */ } }
    } catch (e) { log.debug('[recover] возврат настроек:', e.message); }
  }
}

function _markVolumesDirty(ids) {
  let marked = 0;
  for (const id of ids) {
    const vol = _volumeById(id);
    if (!vol) continue;
    try {
      if (vol.imageData?.modified) { vol.imageData.modified(); marked++; }
      else if (typeof vol.modified === 'function') { vol.modified(); marked++; }
    } catch (e) { log.debug('[recover] пометка:', e.message); }
  }
  return marked;
}

let _recovering = false;

function _report(entry) {
  let rec = entry;
  try { rec = logRecovery(entry) || entry; } catch (e) { /* */ }
  try { if (shouldNotify(rec)) showRecoveryChip(rec); } catch (e) { /* */ }
  return rec;
}

// [v7.0.61] _reinstallOverlays — immediateRender=false
async function _reinstallOverlays(records) {
  const re = getRenderingEngine();
  if (!re || !records.length) return;

  for (const rec of records) {
    const fullId = rec.volumeId.startsWith('cornerstoneStreamingImageVolume:')
      ? rec.volumeId : `cornerstoneStreamingImageVolume:${rec.volumeId}`;
    try {
      if (!cornerstone.cache.getVolume?.(fullId)) {
        const ov = await cornerstone.volumeLoader.createAndCacheVolume(fullId, { imageIds: rec.imageIds });
        ov.load();
      }
    } catch (e) { log.warn(`[recover] наложение ${rec.volumeId}:`, e.message); }
  }
  await new Promise(r => setTimeout(r, 300));

  const mainVolId = _getCurrentVolumeIdFromViewports();
  if (!mainVolId) { log.warn('[recover] основного тома нет — наложения ставить не на что'); return; }
  const entries = [{ volumeId: mainVolId }].concat(records.map(rec => {
    const fullId = rec.volumeId.startsWith('cornerstoneStreamingImageVolume:')
      ? rec.volumeId : `cornerstoneStreamingImageVolume:${rec.volumeId}`;
    const opacity = rec.opts?.opacity ?? 0.5;
    return {
      volumeId: fullId,
      callback: ({ volumeActor }) => {
        try {
          const opFn = volumeActor?.getProperty?.()?.getScalarOpacity?.(0);
          if (opFn) { opFn.removeAllPoints(); opFn.addPoint(0, 0); opFn.addPoint(1, opacity); }
        } catch (e) { log.debug('[recover]', e.message); }
      },
    };
  }));

  const targets = new Set();
  for (const rec of records) for (const id of (rec.viewportIds || [])) targets.add(id);
  const vpIds = [...targets].filter(id => {
    try { const vp = re.getViewport(id); return vp && vp.type !== ViewportType.STACK; } catch (e) { return false; }
  });
  for (const vpId of vpIds) {
    try { await cornerstone.setVolumesForViewports(re, entries, [vpId], false); }
    catch (e) { log.warn(`[recover] наложения в ${vpId}:`, e.message); }
  }
}

// [v7.0.61] activeViewports передаётся в planRecovery
async function _recoverAfterBackground(reason) {
  if (_recovering) return { skipped: 'уже идёт' };
  const re = state.renderingEngine || getRenderingEngine();
  if (!re) return { skipped: 'нет движка рендеринга' };
  _recovering = true;
  try {
    // [9.0.1] Последняя попытка взять на учёт то, что ещё живо: часть томов
    // могла уцелеть, и без записи мы бы объявили их невосстановимыми зря.
    _syncRegistryFromCache('перед восстановлением');

    const shown = _visibleVolumeIds();
    const missing = shown.filter(id => !_volumeById(id));

    const plan = planRecovery({
      shownVolumeIds: shown,
      missingVolumeIds: missing,
      glLostSince: _glLostSinceLastRecover,
      haveLastLoad: planRestore(missing).restorable.length > 0,
      contextStillLost: _glLost,
      activeViewports: getViewportIds().length,
    });
    if (plan.wait) { log.info(`[recover] ${reason}: ${plan.action}`); return plan; }

    if (plan.reload) {
      const { restorable, unrestorable } = planRestore(missing);
      log.warn(`[recover] ${reason}: из кэша пропало ${missing.length} томов — восстанавливаю ${restorable.length}. Случай C.`);
      showToast(`Восстанавливаю том${restorable.length > 1 ? 'а' : ''}…`, 'info', 2500);
      const snap = _snapshotViewports();

      for (const rec of restorable.filter(r => r.kind === 'main')) {
        try {
          await loadVolume(rec.volumeId, rec.imageIds, rec.viewportIds, { ...rec.opts, isRecovery: true });
        } catch (e) { log.warn(`[recover] том ${rec.volumeId} не вернулся:`, e.message); }
      }

      const overlays = restorable.filter(r => r.kind === 'overlay');
      if (overlays.length) {
        try { await _reinstallOverlays(overlays); }
        catch (e) { log.warn('[recover] наложения не вернулись:', e.message); }
      }

      _restoreViewports(snap);
      re.renderViewports(getViewportIds());

      const check = verifyRecovery({
        expected: restorable.map(r => r.volumeId),
        present: missing.filter(id => _volumeById(id)),
        rendered: _visibleVolumeIds().filter(id => _volumeById(id)),
        unrestorable,
      });
      if (!check.ok) log.error(`[recover] восстановление неполное: ${check.text}`);
      return _report({
        case: 'C', action: 'попытка перезагрузить тома из исходных снимков',
        volumes: missing.length, outcome: check.text, ok: check.ok, reason,
      });
    }
    if (plan.case === 'C') {
      // [9.0.0] Раньше этот случай был немым: «нечем восстановить: 1» — и всё.
      // По такому отчёту нельзя понять, ПОЧЕМУ нечем: то ли серию грузили в
      // обход учёта, то ли запись вытеснили, то ли идентификатор тома не
      // совпал с тем, под каким его записали. Теперь в отчёт идут оба списка,
      // и ответ виден сразу, без второго захода.
      const known = knownVolumeIds();
      const shortId = (id) => String(id).length > 46 ? String(id).slice(0, 46) + '…' : String(id);
      const detail = `пропали: ${missing.map(shortId).join(', ') || '—'}; в учёте: ${known.length ? known.map(shortId).join(', ') : 'пусто'}`;
      log.error(`[recover] ${reason}: тома нет в кэше, а исходных снимков не сохранилось. ${detail}`);
      return _report({
        case: 'C-нечем', action: plan.action, volumes: missing.length,
        outcome: `нечем восстановить: ${missing.length} · ${detail}`, ok: false, reason,
      });
    }

    if (plan.resetVolumes) {
      _glLostSinceLastRecover = false;
      log.warn(`[recover] ${reason}: контекст GPU терялся — переустанавливаю тома во вьюпорты. Случай B.`);
      const snap = _snapshotViewports();
      const vpIds = getViewportIds().filter(id => {
        try { return re.getViewport(id)?.type !== ViewportType.STACK; } catch (e) { return false; }
      });
      _markVolumesDirty(shown);
      try {
        await cornerstone.setVolumesForViewports(re, shown.map(volumeId => ({ volumeId })), vpIds, false);
      } catch (e) {
        log.warn('[recover] переустановка томов не удалась:', e.message);
      }
      _restoreViewports(snap);
      re.resize(true, true);
      re.renderViewports(getViewportIds());
      const checkB = verifyRecovery({
        expected: shown,
        present: shown.filter(id => _volumeById(id)),
        rendered: _visibleVolumeIds().filter(id => _volumeById(id)),
      });
      if (!checkB.ok) log.error(`[recover] переустановка неполная: ${checkB.text}`);
      return _report({
        case: 'B', action: 'тома переустановлены во вьюпорты', volumes: shown.length,
        outcome: checkB.text, ok: checkB.ok, reason,
      });
    }

    if (plan.case === 'нет томов') {
      return _report({ case: 'нет томов', action: plan.action, volumes: 0, reason });
    }

    const marked = _markVolumesDirty(shown);
    re.resize(true, true);
    const ids = getViewportIds();
    if (ids.length) re.renderViewports(ids);
    const checkA = verifyRecovery({
      expected: shown,
      present: shown.filter(id => _volumeById(id)),
      rendered: _visibleVolumeIds().filter(id => _volumeById(id)),
    });
    return _report({
      case: 'A', action: 'текстуры помечены к перезаливу', volumes: marked,
      outcome: checkA.text, ok: checkA.ok, reason,
    });
  } catch (e) {
    log.error('[recover] сбой восстановления:', e.message);
    return { error: e.message };
  } finally {
    _recovering = false;
  }
}

/**
 * [9.0.1] Снять учёт с того, что реально показано.
 *
 * Отчёт с рабочего компьютера показал восемь подряд «C-нечем · томов: 3»: три
 * тома на экране, а в учёте пусто. Я чинил это, дописывая rememberVolume в
 * каждое место, где создаётся том, — и раз за разом находил ещё одно. Гоняться
 * за путями загрузки бессмысленно: завтра появится новый.
 *
 * Поэтому смотрим не на то, КАК том попал на экран, а на то, что он на экране
 * есть. У потокового тома Cornerstone сами imageIds лежат в объекте — этого
 * достаточно, чтобы собрать его заново. Что бы его ни создало.
 *
 * Момент вызова важнее самой проверки: снимок делается перед уходом в фон,
 * когда всё ещё живо. Именно там мы раньше и теряли последнюю возможность.
 */
function _syncRegistryFromCache(why) {
  let added = 0;
  try {
    for (const id of _visibleVolumeIds()) {
      if (getVolumeRecord(id)) continue;
      const vol = _volumeById(id);
      const ids = vol?.imageIds;
      if (!Array.isArray(ids) || !ids.length) continue;
      rememberVolume(id, ids, getViewportIds(), {}, 'main');
      try { memoryGuardian.lock(ids); } catch (e) { /* */ }
      added++;
    }
  } catch (e) { log.debug('[registry] снимок:', e.message); }
  if (added) log.info(`[registry] ${why}: взято на учёт томов, загруженных мимо loadVolume: ${added}`);
  return added;
}

document.addEventListener('visibilitychange', () => {
  // Уходим в фон — последний миг, когда всё ещё на месте. Снимаем учёт.
  if (document.visibilityState === 'hidden') { _syncRegistryFromCache('уход в фон'); return; }
  if (document.visibilityState !== 'visible') return;
  setTimeout(() => {
    if (_glLost) {
      log.info('[recover] возврат из фона: контекст GPU ещё не восстановлен — жду события восстановления.');
      return;
    }
    _recoverAfterBackground('возврат из фона');
  }, 300);
}, { signal: state.globalAC.signal });

// ══════════════════════════════════════════════════════════════
//  ГОТОВНОСТЬ ВЬЮПОРТОВ К ПЕРВОМУ ПОКАЗУ
// ══════════════════════════════════════════════════════════════

/**
 * [10.0.2] Дождаться реального размера вьюпортов и пересчитать камеры.
 *
 * Зачем отдельной функцией. На мобильном первый показ MPR был чёрным, и лечили
 * это «агрессивным ре-рендером» — четырьмя вызовами re.resize(true, true) с
 * задержками 100/500/1200/2500 мс. Помочь они не могли ни при каких условиях.
 *
 * В ContextPoolRenderingEngine._resizeVTKViewports движок делает так:
 *     const prevCamera = vp.getCamera();
 *     vp.resetCameraForResize();                 // посчитал ПРАВИЛЬНУЮ камеру
 *     if (keepCamera) vp.setCamera(prevCamera);  // и вернул старую поверх
 *
 * Второй аргумент resize — это и есть keepCamera, по умолчанию true. Если
 * камера родилась, когда элемент вьюпорта имел нулевой размер (мобильная
 * вёрстка со шторками, топбаром и safe-area устаканивается позже первого
 * кадра), она вырожденная — и каждый из четырёх «спасительных» вызовов
 * аккуратно её восстанавливал. Отсюда и то, что срезы появлялись только после
 * ПОВТОРНОЙ загрузки: к тому моменту вёрстка устоялась, и камера считалась
 * правильно с самого начала.
 *
 * keepCamera: true — верное поведение при обычном ресайзе окна и повороте
 * экрана: там пользователь уже настроил зум и панораму, терять их нельзя.
 * Неверно оно ровно в одном случае — при первом показе, и только его эта
 * функция и обслуживает.
 *
 * @param {string[]} vpIds вьюпорты, которые должны быть готовы
 * @param {object} opts { timeoutMs, resetCamera }
 * @returns {Promise<{sized: boolean, waitedMs: number}>}
 */
export async function ensureViewportsReady(vpIds, opts = {}) {
  const { timeoutMs = 3000, resetCamera = true } = opts;
  const re = getRenderingEngine();
  if (!re || !vpIds?.length) return { sized: false, waitedMs: 0 };

  const started = performance.now();

  const allSized = () => vpIds.every(id => {
    try {
      const el = re.getViewport(id)?.element;
      return el && el.offsetWidth > 0 && el.offsetHeight > 0;
    } catch (e) { return false; }
  });

  // Ждём ненулевой размер: ResizeObserver на контейнере плюс страховка по
  // кадрам анимации — размер может поменяться и без события наблюдателя
  if (!allSized()) {
    const container = document.getElementById('viewport-container');
    await new Promise(resolve => {
      let done = false;
      let ro = null;
      let timer = null;
      const finish = () => {
        if (done) return;
        done = true;
        try { ro?.disconnect(); } catch (e) { /* */ }
        clearTimeout(timer);
        resolve();
      };

      if (container && typeof ResizeObserver !== 'undefined') {
        ro = new ResizeObserver(() => { if (allSized()) finish(); });
        ro.observe(container);
      }
      timer = setTimeout(finish, timeoutMs);

      const tick = () => {
        if (done) return;
        if (allSized()) { finish(); return; }
        requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    });
  }

  const sized = allSized();

  // keepCamera: false — пересчитать камеру под фактический размер.
  // Именно этого не делал прежний resize(true, true).
  try { re.resize(true, false); } catch (e) { log.warn('[VP/готовность] resize:', e.message); }

  // [10.0.2] Объёмные окна — отдельной веткой. resize(immediate,false) вернул
  // им камеру «впритык к границам», а нужен тот же масштаб, что ставится после
  // применения пресета: объём обязан быть соразмерен срезам в соседних окнах.
  // _fit3DCamera считает от габаритов, а не от прежнего масштаба, поэтому
  // повторный вызов здесь безопасен.
  for (const id of vpIds) {
    try {
      const vp = re.getViewport(id);
      if (!vp) continue;
      const is3D = String(vp.type || '').toLowerCase().includes('3d');
      if (is3D) { _fit3DCamera(vp); continue; }
      if (resetCamera) vp.resetCamera?.();
    } catch (e) { log.debug('[VP/готовность] resetCamera:', e.message); }
  }

  try { re.renderViewports(vpIds); } catch (e) { log.debug('[VP/готовность] render:', e.message); }

  const waitedMs = Math.round(performance.now() - started);
  if (!sized) {
    log.warn(`[VP/готовность] вьюпорты не получили размер за ${waitedMs} мс — камеры пересчитаны вслепую`);
  } else if (waitedMs > 50) {
    log.info(`[VP/готовность] размер получен за ${waitedMs} мс, камеры пересчитаны`);
  }
  return { sized, waitedMs };
}

// ══════════════════════════════════════════════════════════════
//  3D: пресет + постановка камеры
// ══════════════════════════════════════════════════════════════

/**
 * Габариты того, что реально нарисовано в окне, в мировых координатах.
 * Берём у актёра, а не у тома: у объёмного окна это тот же ящик, но спрашивать
 * его можно в любой момент, не таща сюда размеры и направление тома.
 */
function _actorBounds(vp) {
  try {
    for (const ae of (vp.getActors?.() || [])) {
      const b = ae?.actor?.getBounds?.() || ae?.actor?.getMapper?.()?.getInputData?.()?.getBounds?.();
      if (b?.length === 6 && b.every(Number.isFinite) && b[1] > b[0]) return b;
    }
  } catch (e) { /* нет актёров — нечего мерить */ }
  return null;
}

/**
 * [10.0.2] Вписать объём в окно так, чтобы он был ТОЙ ЖЕ высоты, что срезы
 * в соседних окнах.
 *
 * Что было. После resetCamera стоял постоянный множитель: ×1.30 к
 * parallelScale (или ×1.15 к расстоянию для перспективы). Множитель этот
 * отодвигает камеру, то есть УМЕНЬШАЕТ картинку на треть. В одном окне на
 * пол-экрана это читалось как «с полями»; в раскладке из шести окон рядом со
 * срезами, вписанными вплотную, объём выглядел вдвое мельче соседей — глазу
 * не за что зацепиться, и сравнивать невозможно.
 *
 * Что теперь. Считаем, какой высоты объём в текущем ракурсе камеры, и ставим
 * масштаб так, чтобы он занял заданную долю окна. Ракурс учитывается сам
 * собой: проекции считаются на базис ТЕКУЩЕЙ камеры, поэтому вызывать нужно
 * ПОСЛЕ поворота в стандартный вид, а не до.
 *
 * Горизонталь проверяется тоже: широкий таз в узком окне иначе вылез бы за
 * края. Берём то из двух ограничений, которое жёстче.
 *
 * Функция идемпотентна — считает от габаритов, а не от прежнего масштаба,
 * поэтому повторный вызов (после ресайза, например) ничего не сломает.
 *
 * @param {object} vp3d
 * @param {number} [fill=0.92] какую долю окна занять по стеснённой оси
 * @returns {boolean} удалось ли посчитать по габаритам
 */
// Доля окна берётся ТА ЖЕ, что у срезовых окон (_FRAME_FILL): в этом весь
// смысл — чтобы объём и срез рядом были одной высоты. Разойдутся константы —
// вернётся ровно та разница в размерах, из-за которой всё и переделывалось.
function _fit3DCamera(vp3d, fill = _FRAME_FILL) {
  try {
    const cam = vp3d?.getCamera?.();
    if (!cam?.position || !cam?.focalPoint || !cam?.viewUp) return _frame3DCameraFallback(vp3d);
    const bounds = _actorBounds(vp3d);
    if (!bounds) return _frame3DCameraFallback(vp3d);

    const el = vp3d.element;
    const w = el?.clientWidth || el?.offsetWidth || 0;
    const h = el?.clientHeight || el?.offsetHeight || 0;

    const fit = computeFit3D({
      bounds,
      focalPoint: cam.focalPoint,
      position: cam.position,
      viewUp: cam.viewUp,
      aspect: (w > 0 && h > 0) ? (w / h) : 1,
      fill,
    });
    if (!fit) return _frame3DCameraFallback(vp3d);

    if (cam.parallelProjection) {
      vp3d.setCamera({ parallelScale: fit.need });
    } else {
      const dist = distanceForFit(fit.need, cam.viewAngle);
      if (!dist) return _frame3DCameraFallback(vp3d);
      vp3d.setCamera({ position: cam.focalPoint.map((v, i) => v + fit.dir[i] * dist) });
    }
    return true;
  } catch (err) {
    log.debug('[3D] вписывание камеры:', err.message);
    return _frame3DCameraFallback(vp3d);
  }
}

/**
 * Запасной путь, когда габаритов нет (актёры ещё не построены). Прежний
 * постоянный отступ, но умеренный: ×1.08 вместо ×1.30 — объём всё равно
 * должен быть соразмерен соседним окнам, а не втрое меньше.
 *
 * ВАЖНО: вызывать только сразу после сброса камеры — множитель накапливается.
 */
function _frame3DCameraFallback(vp3d) {
  try {
    const cam = vp3d?.getCamera?.();
    if (!cam) return false;
    if (cam.parallelProjection) {
      if (Number.isFinite(cam.parallelScale)) vp3d.setCamera({ parallelScale: cam.parallelScale * 1.08 });
    } else if (cam.position && cam.focalPoint) {
      const dist = Math.sqrt(cam.position.reduce((acc, val, idx) => acc + (val - cam.focalPoint[idx]) ** 2, 0));
      if (!dist) return false;
      const dir = cam.position.map((val, idx) => (val - cam.focalPoint[idx]) / dist);
      vp3d.setCamera({ position: cam.focalPoint.map((val, idx) => val + dir[idx] * dist * 1.05) });
    }
  } catch (err) { log.debug('[viewport 3d zoom]', err.message); }
  return false;
}

/**
 * [10.0.2] Один объёмный вьюпорт: пресет, камера, стандартный вид.
 *
 * Ровно этот код стоял в loadVolume дважды слово в слово (ранний прогон на
 * четверти кадров и добор в финале, если ранний не сработал). Пока объёмное
 * окно было одно, дублирование просто мозолило глаза; с появлением mpr6 его
 * пришлось бы писать уже вчетверо — по два места на каждое окно.
 *
 * Порядок важен: сначала стандартный ракурс, и только потом вписывание.
 * _fit3DCamera считает габариты в базисе ТЕКУЩЕЙ камеры, и если повернуть
 * окно после неё, объём окажется вписан по прежнему ракурсу.
 */
function _setup3DViewport(re, vpId, presetName) {
  if (!re || !vpId) return false;
  try {
    const vp3d = re.getViewport(vpId);
    if (!vp3d) return false;
    apply3DPreset(vp3d, presetName);
    vp3d.resetCamera();
    _setStdView(vpId, DEFAULT_3D_VIEW);
    _fit3DCamera(vp3d);
    vp3d.render();
    return true;
  } catch (e) {
    log.debug('[3D] постановка окна', vpId, e.message);
    return false;
  }
}

/**
 * [10.0.2] Расставить пресеты по всем объёмным окнам раскладки.
 *
 * @param {object} re — rendering engine
 * @param {{modality?: string, hint?: string}} info — из тегов серии
 * @returns {number} сколько окон удалось поставить
 */
function _setupAll3DViewports(re, info = {}) {
  const ids = find3DViewportIds();
  if (!ids.length) return 0;
  const auto = autoPreset3D(info.modality, info.hint);
  let done = 0;
  ids.forEach((id, i) => {
    // Первое — привычная кость, остальные — подобранный пресет.
    if (_setup3DViewport(re, id, i === 0 ? DEFAULT_3D_PRESET : auto)) done++;
  });
  if (ids.length > 1) log.info(`[3D] окон: ${ids.length}, второе — пресет «${auto}» (${info.modality || '?'})`);
  return done;
}

// ══════════════════════════════════════════════════════════════
//  LOAD VOLUME (MPR/3D)
// ══════════════════════════════════════════════════════════════

export async function loadVolume(volumeId, imageIds, viewportIds, opts = {}) {
  try {
    if (!opts.isRecovery && Array.isArray(imageIds) && imageIds.length) {
      rememberVolume(volumeId, imageIds, viewportIds, opts, 'main');
      try { noteVolumeShown({ volumeId, viewportIds }); } catch (e) { /* */ }
      try { memoryGuardian.lock(imageIds); } catch (e) { log.debug('[Volume] lock:', e.message); }
    }
    const re = getRenderingEngine();
    let sliceStep = opts.sliceStep || 1;
    try {
      // [10.0.6] Правило вынесено в sliceBudget.js: в 10.0.4 появился второй
      // путь постройки объёма («по требованию»), и он про этот даунсэмпл не
      // знал — строил том из полной серии. Теперь правило одно на всех.
      // [10.4.7] Слабая видеокарта — такой же повод проредить, как телефон.
      // Признак берём у самой машины: уровень GPU из детектора, либо прямое
      // сообщение оболочки о том, что WebGL2 включён с оговорками.
      let _слабая = false;
      try {
        const t = String(getGPUInfo?.()?.tier?.name || getGPUInfo?.()?.tier?.label || '').toUpperCase();
        _слабая = t.includes('LOW') || t.includes('MEDIUM') || t.includes('СЛАБ');
      } catch (e) { /* */ }
      const budget = planSliceBudget({ count: imageIds?.length, isMobile: _isMobileScreen(), requestedStep: sliceStep, isWeakGpu: _слабая });
      if (budget.step > sliceStep) {
        log.info(`[Volume] Мобильный даунсэмпл: ${imageIds.length} → ~${budget.usedCount} срезов (шаг ${budget.step})`);
      }
      sliceStep = budget.step;
    } catch (e) { /* */ }
    const onProgress = opts.onProgress || (() => {});
  
    if (state.volumeAbortController) { state.volumeAbortController.abort(); }
    let aborted = false;
    const ac = { abort: () => { aborted = true; } };
    state.volumeAbortController = ac;
  
    events.emit(EVENTS.VOLUME_LOAD_START, { volumeId, imageCount: imageIds.length });
    _mprSourceSliceCount = imageIds?.length || null;
  
    let usedIds = imageIds;
    if (sliceStep > 1) {
      usedIds = imageIds.filter((_, i) => i % sliceStep === 0);
      log.debug(`[Volume] Slice step=${sliceStep}: ${imageIds.length} → ${usedIds.length}`);
    }
  
    const totalFrames = usedIds.length;
  
    onProgress('metadata', 0, `Метаданные: 0/${totalFrames}`);
    let metaLoaded = 0;
    const BATCH = getRecommendedBatchSize();
    for (let i = 0; i < totalFrames; i += BATCH) {
      if (aborted) { events.emit(EVENTS.VOLUME_LOAD_ABORT, { volumeId }); return null; }
      const batch = usedIds.slice(i, i + BATCH);
      await Promise.allSettled(batch.map(id => cornerstone.imageLoader.loadAndCacheImage(id)));
      metaLoaded += batch.length;
      onProgress('metadata', (metaLoaded / totalFrames) * 10, `Метаданные: ${metaLoaded}/${totalFrames}`);
    }
    if (aborted) return null;
  
    onProgress('create', 10, 'Создание объёма...');
    const fullVolumeId = volumeId.startsWith('cornerstoneStreamingImageVolume:') ? volumeId : `cornerstoneStreamingImageVolume:${volumeId}`;
    try { cornerstone.cache.removeVolumeLoadObject(fullVolumeId); } catch (e) { log.debug("[viewport]", e.message); }
  
    try {
      const cacheSize = cornerstone.cache.getCacheSize?.() || 0;
      const maxCache = cornerstone.cache.getMaxCacheSize?.() || 1024 * 1024 * 1024;
      if (cacheSize / maxCache > 0.75) {
        const mb = Math.round(cacheSize / 1048576);
        events.emit(EVENTS.CACHE_WARNING, { usageRatio: cacheSize / maxCache, sizeMB: cacheSize / 1048576 });
        log.warn(`[Volume] Кэш заполнен на ${Math.round(cacheSize / maxCache * 100)}% (${mb} МБ) — чищу только НЕпоказанные тома, показанные не трогаю.`);
        // [10.0.7] Раньше здесь только предупреждали. Непоказанные тома можно
        // отдать безопасно — их вернёт реестр; текущий том защищён keepIds.
        memoryGuardian.reclaim([volumeId]).catch(() => {});
        try { noteMemoryEvent('кэш', 'заполнен', `${Math.round(cacheSize / maxCache * 100)}%, ${mb} МБ — очистка не выполнялась`); } catch (e) { /* */ }
      }
    } catch (e) { log.debug("[viewport]", e.message); }
  
    if (_mixedOrientation(usedIds)) {
      log.warn('[Volume] Смешанные ориентации срезов в серии — MPR/3D пропущены (локалайзер?)');
      showToast('В серии срезы разных плоскостей (локалайзер) — MPR/3D недоступны, просмотр стопкой работает', 'warning', 4500);
      events.emit(EVENTS.VOLUME_LOAD_ABORT, { volumeId, reason: 'mixed-orientation' });
      document.getElementById('viewport-container')?.classList.remove('vol-warming');
      return null;
    }

    const volume = await cornerstone.volumeLoader.createAndCacheVolume(fullVolumeId, { imageIds: usedIds });
    if (aborted) return null;
  
    onProgress('viewports', 16, 'Назначение вьюпортов...');
    // [10.0.2] find3DViewportIds (множественное) вместо find3DViewportId:
    // в раскладке mpr6 объёмных окна два, и со старым вызовом второе
    // оставалось бы без тома
    const allViewportIds = [...viewportIds, ...find3DViewportIds()]
      .filter((id, i, arr) => id && arr.indexOf(id) === i && re.getViewport(id) !== undefined);
  
    let _wasCrosshairs = false;
    try {
      const { ToolGroupManager } = cornerstoneTools;
      const tg = ToolGroupManager?.getToolGroup?.(TOOL_GROUP_ID);
      if (tg) { const to = tg.getToolOptions?.('Crosshairs'); _wasCrosshairs = to?.mode === 'Enabled' || to?.mode === 1; if (_wasCrosshairs) tg.setToolDisabled('Crosshairs'); }
    } catch (e) { log.debug("[viewport]", e.message); }
  
    for (const vpId of allViewportIds) {
      const vp = re.getViewport(vpId);
      if (vp?.element && (vp.element.offsetWidth === 0 || vp.element.offsetHeight === 0)) {
        re.resize(true, true); await new Promise(r => requestAnimationFrame(r)); break;
      }
    }
  
    const _initVOI = _initialVOIFromTags(usedIds, opts.modality);
    if (_initVOI) log.info(`[AutoWL/старт] тег-окно вшито до первого показа ${Math.round(_initVOI.lower)}..${Math.round(_initVOI.upper)}`);

    const _warmC = document.getElementById('viewport-container');
    _warmC?.classList.add('vol-warming');
    setTimeout(() => _warmC?.classList.remove('vol-warming'), 15000);

    await cornerstone.setVolumesForViewports(re, [{
      volumeId: fullVolumeId,
      callback: _initVOI ? (({ volumeActor }) => {
        try { volumeActor.getProperty().getRGBTransferFunction(0).setMappingRange(_initVOI.lower, _initVOI.upper); }
        catch (e) { log.debug('[AutoWL/старт/actor]', e.message); }
      }) : undefined,
    }], allViewportIds, false);

    try {
      try {
        const dim = volume.dimensions, sp = volume.spacing, org = volume.origin, dir = volume.direction;
        if (dim?.length === 3 && sp?.length === 3) {
          const ext = [dim[0] * sp[0], dim[1] * sp[1], dim[2] * sp[2]];
          _mprVolumeHalfExtent = Math.max(ext[0], ext[1], ext[2]) / 2;
          if (org?.length === 3 && dir?.length === 9) {
            const h = [ext[0] / 2, ext[1] / 2, ext[2] / 2];
            _mprVolumeCenter = [
              org[0] + dir[0] * h[0] + dir[3] * h[1] + dir[6] * h[2],
              org[1] + dir[1] * h[0] + dir[4] * h[1] + dir[7] * h[2],
              org[2] + dir[2] * h[0] + dir[5] * h[1] + dir[8] * h[2],
            ];
          }
        }
      } catch (e) { _mprVolumeHalfExtent = null; _mprVolumeCenter = null; }
      _mprAlignMode = 'acquired';
      const tiltDeg = applyMprAlignment(volume.direction);
      if (tiltDeg > 2) log.info(`[MPR] Серия наклонена на ${tiltDeg.toFixed(1)}° — режим: ${_mprAlignMode === 'body' ? 'по телу' : 'по съёмке'}`);
    } catch (e) { log.warn('[MPR] выравнивание:', e.message); }

    try {
      for (const _vpM of allViewportIds) {
        const vpM = re.getViewport(_vpM);
        if (!vpM || String(vpM.type || '').toLowerCase().includes('3d')) continue;
        _fitToFrame(vpM);
      }
    } catch (e) { log.debug('[MPR] поля:', e.message); }
    if (_initVOI) {
      for (const _vpId0 of allViewportIds) {
        try {
          const _vp0 = re.getViewport(_vpId0);
          if (!_vp0 || String(_vp0.type || '').toLowerCase().includes('3d')) continue;
          _vp0.setProperties?.({ voiRange: { lower: _initVOI.lower, upper: _initVOI.upper } });
        } catch (e) { log.debug('[AutoWL/старт/vp]', e.message); }
      }
    }
    try {
      for (const _vpS of allViewportIds) {
        const vpS = re.getViewport(_vpS);
        const elS = vpS?.element;
        if (!elS || String(vpS.type || '').toLowerCase().includes('3d')) continue;
        const spy = (ev) => {
          const r = ev.detail?.range || ev.detail?.voiRange || {};
          log.warn('[VOI-шпион]', _vpS, `${Math.round(r.lower ?? NaN)}..${Math.round(r.upper ?? NaN)}`);
        };
        elS.addEventListener(cornerstone.Enums.Events.VOI_MODIFIED, spy);
        setTimeout(() => elS.removeEventListener(cornerstone.Enums.Events.VOI_MODIFIED, spy), 6000);
      }
    } catch (e) { log.debug('[шпион]', e.message); }
    re.renderViewports(allViewportIds);
    onProgress('viewports', 18, 'Загрузка кадров...');
  
    let loadedFrames = 0;
    let presetApplied = false;

    // [10.0.2] Модальность и описание серии нужны до конца загрузки: пресет
    // второго объёмного окна ставится уже на четверти кадров, вместе с первым.
    const _series3D = (() => {
      let mod = opts.modality, desc = opts.seriesDesc || '', part = opts.bodyPart || '';
      try {
        const g = cornerstone.metaData.get('generalSeriesModule', usedIds[0]) || {};
        mod = mod || g.modality;
        desc = desc || g.seriesDescription || '';
        part = part || g.bodyPartExamined || '';
      } catch (e) { log.debug('[3D] теги серии:', e.message); }
      return { modality: (mod || 'CT').toUpperCase(), hint: `${part} ${desc}`.trim() };
    })();

    const progressHandler = (evt) => {
      const imageId = evt?.detail?.image?.imageId || '';
      if (imageId && !usedIds.includes(imageId)) return;
      loadedFrames++;
      const pct = 18 + (loadedFrames / totalFrames) * 77;
      onProgress('loading', pct, `Кадры: ${loadedFrames}/${totalFrames}`);
      events.emit(EVENTS.VOLUME_LOAD_PROGRESS, { volumeId, phase: 'loading', percent: pct, loaded: loadedFrames, total: totalFrames });
  
      const renderInterval = totalFrames > 200 ? 20 : (totalFrames > 50 ? 5 : 1);
      if (loadedFrames % renderInterval === 0 || loadedFrames === totalFrames) re.renderViewports(allViewportIds);
  
      if (!presetApplied && loadedFrames >= Math.ceil(totalFrames * 0.25)) {
        presetApplied = true;   // как и раньше: одна попытка на загрузку
        try {
          // [10.0.2] Все объёмные окна, а не только первое: в mpr6 их два.
          _setupAll3DViewports(re, _series3D);
        } catch (e) { log.warn('[Volume] Early 3D preset:', e.message); }
      }
    };
  
    cornerstone.eventTarget.addEventListener(cornerstone.Enums.Events.IMAGE_LOADED, progressHandler);
    try {
      volume.load();
      await new Promise((resolve) => {
        let resolved = false;
        const done = () => { if (!resolved) { resolved = true; resolve(); } };
        const iv = setInterval(() => { if (aborted || loadedFrames >= totalFrames) { clearInterval(iv); done(); } }, 200);
        setTimeout(() => { clearInterval(iv); done(); }, Math.min(totalFrames * 120 + 15000, 180000));
      });
    } finally {
      cornerstone.eventTarget.removeEventListener(cornerstone.Enums.Events.IMAGE_LOADED, progressHandler);
    }
  
    if (aborted || state.volumeAbortController !== ac) return null;
  
    onProgress('preset', 95, 'Финализация...');
    re.renderViewports(allViewportIds);
  
    if (!presetApplied) {
      // Ранний прогон не дошёл (мало кадров или загрузка оборвалась по таймауту)
      try { _setupAll3DViewports(re, _series3D); } catch (e) { log.debug('[viewport]', e.message); }
    }
  
    onProgress('done', 100, `✅ ${loadedFrames}/${totalFrames} кадров`);
  
    try {
      let sd = null;
      try { sd = volume.voxelManager?.getCompleteScalarDataArray?.(); } catch (e) { log.debug("[viewport]", e.message); }
      if (!sd && volume.imageData) try { sd = volume.imageData.getPointData?.()?.getScalars?.()?.getData?.(); } catch (e) { log.debug("[viewport]", e.message); }
      if (!sd) try { sd = volume.getScalarData?.(); } catch (e) { log.debug("[viewport]", e.message); }
      if (sd?.length > 0) {
        _computeHistogram(sd);
        // [10.0.3] Запоминаем, в каких числах вообще лежит серия. Нужно, чтобы
        // понять, попадёт ли в них объёмный пресет: КТ-пресеты написаны в HU,
        // а у МР своя шкала, и «Кожа» с окном −200…+200 промахивалась мимо
        // всего — окно чернело без единой ошибки в журнале.
        _volumeValueRange = _scanValueRange(sd);
      }
    } catch (e) { log.debug("[viewport]", e.message); }
  
    try { const vp3d = re.getViewport(find3DViewportId()); if (vp3d) _initCinematic(vp3d); } catch (e) { log.debug("[viewport]", e.message); }
  
    let modality = opts.modality;
    if (!modality) {
      try { modality = cornerstone.metaData.get('generalSeriesModule', usedIds[0])?.modality; } catch (e) { log.debug("[viewport]", e.message); }
    }
    modality = (modality || 'CT').toUpperCase();
    _volumeModality = modality;   // [10.0.3] нужна при применении объёмных пресетов
    try {
      const { workflow } = getDefaultForModality(modality);
      if (workflow && getCinematicRenderer()) {
        await applyWorkflow(workflow.id, { viewport3D: re.getViewport(find3DViewportId()), cinematicRenderer: getCinematicRenderer() });
      }
    } catch (e) { log.debug("[viewport]", e.message); }
  
    try {
      if (getCinematicRenderer()) {
        autoSelectPreset(getCinematicRenderer(), { modality, seriesDesc: opts.seriesDesc || '', bodyPart: opts.bodyPart || '', sliceThickness: opts.sliceThickness || 0 }, { animate: true, duration: 800 });
      }
    } catch (e) { log.debug("[viewport]", e.message); }

    if (modality !== 'CT') {
      const applyDataVOI = (tag) => {
        try {
          let lo = null, hi = null, src = '';
          try {
            const midId = usedIds[Math.floor(usedIds.length / 2)];
            const voi = cornerstone.metaData.get('voiLutModule', midId);
            let wc = Array.isArray(voi?.windowCenter) ? voi.windowCenter[0] : voi?.windowCenter;
            let ww = Array.isArray(voi?.windowWidth) ? voi.windowWidth[0] : voi?.windowWidth;
            wc = parseFloat(wc); ww = parseFloat(ww);
            if (Number.isFinite(wc) && Number.isFinite(ww) && ww > 0) { lo = wc - ww / 2; hi = wc + ww / 2; src = 'DICOM-теги'; }
          } catch (e) { log.debug('[AutoWL/Volume] tags:', e.message); }

          let lo3 = null, hi3 = null;
          try {
            let sd = null;
            try { sd = volume.voxelManager?.getCompleteScalarDataArray?.(); } catch (e) { log.debug('[AutoWL/Volume]', e.message); }
            if (!sd && volume.imageData) try { sd = volume.imageData.getPointData?.()?.getScalars?.()?.getData?.(); } catch (e) { log.debug('[AutoWL/Volume]', e.message); }
            if (sd?.length > 0) {
              const step = Math.max(1, Math.floor(sd.length / 60000));
              const all = [];
              for (let i = 0; i < sd.length; i += step) all.push(sd[i]);
              all.sort((a, b) => a - b);
              const p99 = all[Math.floor(all.length * 0.99)];
              const thr = p99 * 0.06;
              const fore = all.filter(v => v > thr);
              const arr = fore.length > 200 ? fore : all;
              const d2 = arr[Math.floor(arr.length * 0.02)];
              const d99 = arr[Math.floor(arr.length * 0.99)];
              const d995 = arr[Math.floor(arr.length * 0.995)];
              lo3 = Math.max(0, thr); hi3 = d995;
              if (Number.isFinite(lo3) && Number.isFinite(hi3) && hi3 > lo3) _mrTissueRange = { lo: lo3, hi: hi3 };
              if (lo === null && Number.isFinite(d2) && Number.isFinite(d99) && d99 > d2) { lo = d2; hi = d99; src = `ткань>${Math.round(thr)}`; }
            }
          } catch (e) { log.debug('[AutoWL/Volume] data:', e.message); }

          if (!(Number.isFinite(lo) && Number.isFinite(hi) && hi > lo)) return;
          const _re2 = getRenderingEngine();
          const report = [];
          for (const vpId of (getViewportIds() || [])) {
            try {
              const vp = _re2?.getViewport(vpId);
              if (!vp) continue;
              const is3D = String(vp.type || '').toLowerCase().includes('3d');
              if (is3D) {
                try {
                  const L3 = Number.isFinite(lo3) ? lo3 : lo;
                  const H3 = (Number.isFinite(hi3) && hi3 > L3) ? hi3 : hi;
                  vp.setBlendMode?.(cornerstone.Enums.BlendModes.MAXIMUM_INTENSITY_BLEND);
                  try {
                    const prop = vp.getDefaultActor?.()?.actor?.getProperty?.();
                    if (prop) {
                      const o = prop.getScalarOpacity(0);
                      o.removeAllPoints(); o.addPoint(L3, 0.0); o.addPoint(H3, 1.0);
                      prop.getRGBTransferFunction(0).setMappingRange(L3, H3);
                    }
                  } catch (e4) { log.debug('[AutoWL/3D/tf]', e4.message); }
                  vp.setProperties?.({ voiRange: { lower: L3, upper: H3 } });
                  vp.render?.();
                  report.push(`${vpId}=MIP ${Math.round(L3)}..${Math.round(H3)}`);
                } catch (e3) { log.warn('[AutoWL/3D]', vpId, e3.message); }
                continue;
              }
              vp.setProperties?.({ voiRange: { lower: lo, upper: hi } });
              vp.render?.();
              const cur = vp.getProperties?.()?.voiRange;
              report.push(`${vpId}=${cur ? Math.round(cur.lower) + '..' + Math.round(cur.upper) : '?'}`);
            } catch (e) { log.warn('[AutoWL/Volume]', vpId, e.message); }
          }
          log.info(`[AutoWL/Volume] ${modality} ${tag} (${src}): 2D ${Math.round(lo)}..${Math.round(hi)} | ${report.join(' ')}`);
        } catch (e) { log.warn('[AutoWL/Volume] не сработало:', e.message); }
      };
      applyDataVOI('сразу');
      setTimeout(() => applyDataVOI('через 1.2с'), 1200);
      setTimeout(() => {
        const cW = document.getElementById('viewport-container');
        cW?.classList.remove('vol-warming');
        log.info('[Занавес] снят — окно финальное (после контрольного применения)');
      }, 1350);
    } else {
      const cCT = document.getElementById('viewport-container');
      requestAnimationFrame(() => requestAnimationFrame(() => { cCT?.classList.remove('vol-warming'); log.info('[Занавес] снят (КТ)'); }));
    }
  
    events.emit(EVENTS.VOLUME_LOAD_DONE, { volumeId, framesLoaded: loadedFrames });
  
    try { activateDataTools(); } catch (e) { log.debug("[viewport]", e.message); }
  
    if (state.volumeAbortController === ac) state.volumeAbortController = null;
    return volume;
  } catch (e) {
    log.warn(`[viewport] loadVolume failed:`, e.message);
    try { showToast('Объём не построен: ' + (e?.message || e), 'error', 6000); } catch (_) { /* */ }
    return null;
  }
}

// [v7.0.61] _computeHistogram с AbortController
let _histogramAC = null;

function _computeHistogram(scalarData) {
  if (_histogramAC) _histogramAC.abort();
  _histogramAC = new AbortController();
  const signal = _histogramAC.signal;

  const BINS = 4096, HU_MIN = -1024;
  const hist = new Uint32Array(BINS);
  const step = scalarData.length > 5000000 ? Math.ceil(scalarData.length / 5000000) : 1;
  const CHUNK = 500000;
  let offset = 0;

  function processChunk() {
    if (signal.aborted) return;
    const end = Math.min(offset + CHUNK * step, scalarData.length);
    for (let i = offset; i < end; i += step) {
      const bin = Math.round(scalarData[i] - HU_MIN);
      if (bin >= 0 && bin < BINS) hist[bin]++;
    }
    offset = end;
    if (offset < scalarData.length) {
      setTimeout(processChunk, 0);
    } else {
      if (signal.aborted) return;
      if (step > 1) for (let i = 0; i < BINS; i++) hist[i] = Math.round(hist[i] * step);
      events.emit(EVENTS.HISTOGRAM_READY, { data: hist, min: HU_MIN, max: HU_MIN + BINS - 1 });
    }
  }
  processChunk();
}
// ══════════════════════════════════════════════════════════════
//  ОБЪЁМ ПО ТРЕБОВАНИЮ
// ══════════════════════════════════════════════════════════════

/**
 * [10.0.12] Стартовое окно яркости из тегов серии — для не-КТ.
 *
 * У КТ шкала HU одна на всех, а у МР своя и произвольная (у серии с экрана —
 * до 6600). Пока окно не задано, цветовая функция объёма остаётся в диапазоне
 * пресета, написанном в HU, и ВСЕ воксели МР-серии уезжают в один конец
 * шкалы: вместо головы получается ровный полупрозрачный столб.
 *
 * Раньше это жило прямо в теле loadVolume, поэтому путь MPR лечился, а путь
 * «объём по требованию» — нет. Вынесено, чтобы копия была одна.
 */
function _initialVOIFromTags(imageIds, modality) {
  try {
    const mod0 = (modality
      || cornerstone.metaData.get('generalSeriesModule', imageIds[0])?.modality
      || 'CT').toUpperCase();
    if (mod0 === 'CT') return null;
    const midId = imageIds[Math.floor(imageIds.length / 2)];
    const voi = cornerstone.metaData.get('voiLutModule', midId);
    let wc = Array.isArray(voi?.windowCenter) ? voi.windowCenter[0] : voi?.windowCenter;
    let ww = Array.isArray(voi?.windowWidth) ? voi.windowWidth[0] : voi?.windowWidth;
    wc = parseFloat(wc); ww = parseFloat(ww);
    if (!Number.isFinite(wc) || !Number.isFinite(ww) || ww <= 0) return null;
    return { lower: wc - ww / 2, upper: wc + ww / 2 };
  } catch (e) { log.debug('[AutoWL/старт]', e.message); return null; }
}

/**
 * [10.0.12] Запомнить, в каких числах лежит серия и какая у неё модальность.
 *
 * От этих двух фактов зависит применение объёмных пресетов: по ним
 * _prepareRangedPreset решает, попадёт ли HU-кривая в шкалу серии. Ставил их
 * только loadVolume — поэтому после старта со стопки, когда объём собирает
 * ensureVolumeReady, модальность оставалась пустой, диапазон нулевым, и
 * пресет уходил в окно вслепую.
 */
function _rememberVolumeFacts(volume, imageIds, modality) {
  try {
    let mod = modality;
    if (!mod) { try { mod = cornerstone.metaData.get('generalSeriesModule', imageIds[0])?.modality; } catch (e) { /* */ } }
    _volumeModality = (mod || 'CT').toUpperCase();

    let sd = null;
    try { sd = volume?.voxelManager?.getCompleteScalarDataArray?.(); } catch (e) { /* */ }
    if (!sd && volume?.imageData) try { sd = volume.imageData.getPointData?.()?.getScalars?.()?.getData?.(); } catch (e) { /* */ }
    if (!sd) try { sd = volume?.getScalarData?.(); } catch (e) { /* */ }
    if (sd?.length > 0) _volumeValueRange = _scanValueRange(sd);
    log.info(`[Объём] шкала серии: ${_volumeModality}, ${_volumeValueRange ? `${Math.round(_volumeValueRange.lo)}…${Math.round(_volumeValueRange.hi)}` : 'не определена'}`);
  } catch (e) { log.debug('[Объём] факты серии:', e.message); }
}

/** Телефонный экран — то же условие, что у мобильного даунсэмпла в loadVolume. */
function _isMobileScreen() {
  try { return !!(window.matchMedia && window.matchMedia('(max-width: 768px)').matches); }
  catch (e) { return false; }
}

/**
 * Смешанные ориентации срезов (локалайзер в серии) — из такой серии объём
 * собирать нельзя: реформаты выйдут кашей. Пробуем первую, среднюю и
 * последнюю позиции — этого достаточно, чтобы поймать вклеенный локалайзер,
 * и не нужно читать метаданные всей серии.
 */
function _mixedOrientation(usedIds) {
  try {
    const iopOf = (id) => cornerstone.metaData.get('imagePlaneModule', id)?.imageOrientationPatient;
    const ref = iopOf(usedIds[0]);
    if (!(ref?.length === 6)) return false;
    const same = (b) => Array.isArray(b) && b.length === 6 && ref.every((v, i) => Math.abs(v - b[i]) < 1e-3);
    const probeIds = usedIds.length <= 3
      ? usedIds.slice(1)
      : [usedIds[Math.floor(usedIds.length / 2)], usedIds[usedIds.length - 1]];
    for (const id of probeIds) if (!same(iopOf(id))) return true;
    return false;
  } catch (e) { log.debug('[Volume] orientation check skipped:', e.message); return false; }
}

/**
 * [10.0.4] Убедиться, что объём построен, и дождаться кадров.
 *
 * Зачем это появилось. На телефоне программа открывается в режиме одиночной
 * стопки: грузится нативная серия, объём НЕ строится вовсе. Дальше человек
 * нажимает «Аксиальная» — и получает чёрное окно. Причина не в отрисовке:
 * switchViewMode привязывает окну том по идентификатору, а идентификатора нет
 * (он присваивается только в ветке реформатов), и вся ветка построения просто
 * не выполняется. Ошибки при этом нет ни одной — отсюда и чёрный экран молча.
 *
 * Стоило нажать MPR — и том строился, потому что та ветка его и создаёт. А
 * «в пять раз быстрее» он строился оттого, что все кадры уже лежали в кэше
 * после загрузки стопки: заново качать нечего, остаётся собрать объём.
 *
 * Теперь объём строится по требованию — тем, кто его попросил.
 *
 * @param {string} volumeId
 * @param {string[]} imageIds
 * @param {{onProgress?: Function, timeoutMs?: number}} [opts]
 * @returns {Promise<string|null>} полный идентификатор тома или null
 */
export async function ensureVolumeReady(volumeId, imageIds, opts = {}) {
  if (!volumeId || !Array.isArray(imageIds) || !imageIds.length) return null;
  const { onProgress, timeoutMs = 180000, viewportIds = [] } = opts;
  const fullId = volumeId.startsWith('cornerstoneStreamingImageVolume:')
    ? volumeId : `cornerstoneStreamingImageVolume:${volumeId}`;

  const report = (pct, detail) => { try { onProgress?.(pct, detail); } catch (e) { /* */ } };

  let vol = null;
  try { vol = cornerstone.cache.getVolume?.(fullId); } catch (e) { /* */ }

  if (vol?.loadStatus?.loaded) return fullId;   // уже готов — и уже на учёте у того, кто строил

  if (!vol) {
    // [10.0.6] Этот путь обязан заботиться о томе так же, как loadVolume.
    //
    // В 10.0.4 он появился «голым», и на телефоне это дало сразу три беды:
    //
    //  1. Полная серия вместо бюджета срезов. Путь MPR прореживает серию до
    //     ~192 срезов, здесь же объём строился из всех пятисот — в два с
    //     половиной раза тяжелее. Память с ~80% уходила за 100%.
    //  2. Ни записи в реестре томов, ни замка на кадрах. Для стража памяти и
    //     восстановления такой том НЕ СУЩЕСТВОВАЛ: освободить его при нехватке
    //     памяти нельзя (реестр пуст), а после сворачивания браузера вернуть
    //     нечем — «Случай C-нечем: исходных снимков не сохранилось». Отсюда
    //     «объём перестал держаться в фоне», хотя раньше держался.
    //  3. Без проверки ориентаций локалайзер в серии превращал реформаты в
    //     кашу вместо честного отказа.
    //
    // Занавес от белой вспышки — забота вызывающего (switchViewMode): это
    // вещь экранная, а не расчётная.
    const budget = planSliceBudget({
      count: imageIds.length,
      isMobile: _isMobileScreen(),
      requestedStep: opts.sliceStep || 1,
    });
    const usedIds = thinByStep(imageIds, budget.step);
    if (budget.step > 1) {
      log.info(`[Объём] ${budget.reason}: ${imageIds.length} → ${usedIds.length} срезов (шаг ${budget.step})`);
    }

    if (_mixedOrientation(usedIds)) {
      log.warn('[Объём] Смешанные ориентации срезов — объём не строю (локалайзер?)');
      showToast('В серии срезы разных плоскостей (локалайзер) — реформаты недоступны, просмотр стопкой работает', 'warning', 4500);
      return null;
    }

    // На учёт — ДО постройки: если браузер выгрузит вкладку посреди сборки,
    // восстановлению уже будет от чего оттолкнуться.
    try {
      // [10.1.8] Помним и бережём ВЕСЬ список, а строим из прореженного.
      //
      // Было наоборот — и это расходилось с путём MPR, где всегда берётся
      // полный список. Разница не косметическая: замок стража защищает кадры
      // от вытеснения, и при прореживании под защитой оказывалась только
      // половина. Вторую половину — ту, которую листает стопка, — страж
      // считал ничьей и отдавал первой же при нехватке памяти. Стопка тут же
      // запрашивала эти кадры снова, они грузились и раскладывались заново,
      // и так по кругу. Отсюда и странность, о которой вы написали: с полной
      // серией память держится ровно, а с прореженной — хуже.
      //
      // sliceStep в записи остаётся: по нему восстановление знает, из чего
      // объём был собран.
      rememberVolume(volumeId, imageIds, viewportIds, { sliceStep: budget.step }, 'main');
      memoryGuardian.lock(imageIds);
      noteVolumeShown({ volumeId, viewportIds });
    } catch (e) { log.debug('[Объём] учёт:', e.message); }

    report(2, 'Подготовка объёма…');
    try {
      vol = await cornerstone.volumeLoader.createAndCacheVolume(fullId, { imageIds: usedIds });
    } catch (e) {
      log.warn('[Объём] не удалось создать:', e.message);
      return null;
    }
  }

  try { if (!vol.loadStatus?.loading && !vol.loadStatus?.loaded) vol.load(); }
  catch (e) { log.debug('[Объём] load:', e.message); }

  // Ждём кадры. Они, скорее всего, уже в кэше после загрузки стопки, поэтому
  // ожидание короткое; но полагаться на это нельзя — серия могла быть другой.
  //
  // Счёт кадров — у самого тома, а не у переданного списка: том мог быть
  // построен раньше и с прореживанием, тогда его кадров меньше, чем в серии,
  // и ожидание «всей серии» не кончилось бы никогда.
  const total = vol.imageIds?.length || imageIds.length;
  const started = performance.now();
  await new Promise((resolve) => {
    const tick = () => {
      const st = vol.loadStatus || {};
      const done = Array.isArray(st.cachedFrames)
        ? st.cachedFrames.filter(Boolean).length
        : (st.loaded ? total : 0);
      report(5 + Math.min(90, (done / Math.max(1, total)) * 90), `Кадры: ${done}/${total}`);
      if (st.loaded || done >= total || performance.now() - started > timeoutMs) { resolve(); return; }
      setTimeout(tick, 120);
    };
    tick();
  });

  // [10.0.12] Те же факты о серии, что записывает loadVolume. Без них
  // применение объёмного пресета идёт вслепую: модальность пустая, диапазон
  // нулевой — и HU-кривая ложится мимо МР-шкалы.
  _rememberVolumeFacts(vol, imageIds, opts.modality);

  report(100, 'Готово');
  return fullId;
}

// ══════════════════════════════════════════════════════════════
//  SWITCH VIEW MODE (single fullscreen viewport)
// ══════════════════════════════════════════════════════════════

export async function switchViewMode(containerId, mode, imageIds, volumeId, opts = {}) {
  try {
    _bindLiveSliceOverlay();
    setTimeout(refreshGridOverlays, 60);
    const container = document.getElementById(containerId);
    if (!container) throw new Error(`Container #${containerId} not found`);
    const re = getRenderingEngine();
  
    cleanupViewports(re);
    container.innerHTML = '';
    container.style.display = 'grid';
    container.style.gridTemplateRows = '1fr';
    container.style.gridTemplateColumns = '1fr';
    container.style.gap = '0';
  
    const vpId = `view-${mode}`;
    const el = document.createElement('div');
    el.id = `viewport-${vpId}`;
    el.dataset.viewportId = vpId;
    el.style.cssText = 'width:100%;height:100%;position:relative;background:#000;touch-action:none;user-select:none;-webkit-user-select:none;';

    const _modeLabel = document.createElement('div');
    _modeLabel.className = 'vp-orientation-label';
    _modeLabel.textContent = ({ '2d': 'Нативные срезы', axial: 'Axial', sagittal: 'Sagittal', coronal: 'Coronal', '3d': '3D Volume' })[mode] || mode;
    el.appendChild(_modeLabel);
  
    // [10.0.3] Одиночный режим строит те же кнопки, что и раскладки: раньше
    // здесь была своя разметка (.vp-zoom-controls — плашка по центру снизу),
    // и одна и та же программа выглядела по-разному в зависимости от того,
    // как открыто окно.
    el.appendChild(_buildViewportControls(vpId, mode === '3d'));
    container.appendChild(el);
  
    let type, orientation;
    switch (mode) {
      case 'axial':    type = ViewportType.ORTHOGRAPHIC; orientation = OrientationAxis.AXIAL; break;
      case 'sagittal': type = ViewportType.ORTHOGRAPHIC; orientation = OrientationAxis.SAGITTAL; break;
      case 'coronal':  type = ViewportType.ORTHOGRAPHIC; orientation = OrientationAxis.CORONAL; break;
      case '3d':       type = ViewportType.VOLUME_3D;    orientation = undefined; break;
      default:         type = ViewportType.STACK;         orientation = undefined;
    }
  
    void container.offsetHeight;
    re.setViewports([{ viewportId: vpId, type, element: el, defaultOptions: { orientation, background: [0, 0, 0] } }]);
    state.viewports.set(vpId, { element: el, type, orientation });
  
    await awaitableResize(re, container);
    _attachContextLossHandlers();
    try { initTools([vpId], RENDERING_ENGINE_ID); activateDataTools(); } catch (e) { log.debug("[viewport]", e.message); }
  
    if (el.offsetWidth === 0 || el.offsetHeight === 0) {
      await new Promise(r => setTimeout(r, 300));
      re.resize(true, true);
    }
  
    if (imageIds?.length) {
      if (type === ViewportType.STACK) {
        try { await loadStack(vpId, imageIds); } catch (e) { log.warn('[switchViewMode] stack:', e); }
      } else if (volumeId) {
        // [10.0.6] Занавес — тот же, что на пути MPR (loadVolume). Пока том
        // стримится и пресет ещё не применён, объёмное окно рисует белое
        // облако с передаточной функцией по умолчанию. Путь «по требованию»
        // в 10.0.4 занавес не опускал — и вспышка стала видна, хотя защита в
        // программе есть. Полоса загрузки при этом остаётся видимой: занавес
        // прячет только канвы.
        const _curtainOk = container.id === 'viewport-container';
        if (_curtainOk) container.classList.add('vol-warming');
        const _curtainSafety = setTimeout(() => {
          if (container.classList.contains('vol-warming')) {
            container.classList.remove('vol-warming');
            log.warn('[Занавес] снят страховкой (15с, объём по требованию)');
          }
        }, 15000);
        const _liftCurtain = (why) => {
          clearTimeout(_curtainSafety);
          if (container.classList.contains('vol-warming')) {
            container.classList.remove('vol-warming');
            log.info(`[Занавес] снят (${why})`);
          }
        };
        try {
          // [10.0.4] Ждём НАСТОЯЩЕЙ готовности кадров, а не полсекунды наугад.
          //
          // Было: vol.load(); await 500 мс — и сразу привязка к окну. Если
          // кадры не успевали (а на серии в сотни срезов они не успевают),
          // окно оставалось пустым, и понять это было нельзя: ни ошибки, ни
          // полосы загрузки.
          const fullId = await ensureVolumeReady(volumeId, imageIds, {
            onProgress: opts.onProgress,
            viewportIds: [vpId],          // [10.0.6] учёт: кто показывает том — тому и восстанавливать
            sliceStep: opts.sliceStep,
          });
          if (!fullId) { _liftCurtain('объём не построен'); log.warn('[switchViewMode] объём не построен'); return vpId; }
          // [10.0.12] Стартовое окно — как в loadVolume. На МР без него цветовая
          // функция остаётся в диапазоне пресета (он написан в HU), все воксели
          // уезжают в один конец шкалы, и объём выглядит ровным полупрозрачным
          // столбом вместо головы. Путь MPR это делал, мой — нет.
          const _voi = _initialVOIFromTags(imageIds, opts.modality);
          await cornerstone.setVolumesForViewports(re, [{
            volumeId: fullId,
            callback: _voi ? (({ volumeActor }) => {
              try { volumeActor.getProperty().getRGBTransferFunction(0).setMappingRange(_voi.lower, _voi.upper); }
              catch (e) { log.debug('[AutoWL/старт/actor]', e.message); }
            }) : undefined,
          }], [vpId]);
          if (type === ViewportType.VOLUME_3D) {
            const vp = re.getViewport(vpId);
            if (vp) {
              // [10.0.10] Кнопка «3D» открывает объём с костью (как пятое окно
              // MPR 6), кнопка «3D пресет» — с автоматически подобранным
              // пресетом (как шестое). Подбор тот же, что в mpr6: по
              // модальности и описанию серии, из тегов первого снимка.
              // [10.0.13] Одиночное окно снова показывает ровно то, что
              // показывало всегда, — кость. Своей ветки «с автопресетом»
              // здесь больше нет: она трижды расходилась с рабочим окном
              // mpr6, и кнопка «3D пресет» теперь просто разворачивает то
              // самое шестое окно вместо того, чтобы воспроизводить его.
              _setup3DViewport(re, vpId, DEFAULT_3D_PRESET);
              _initCinematic(vp);
            }
          }
          re.renderViewports([vpId]);
          // Снимаем после двух кадров: к этому моменту пресет уже применён и
          // отрисован — облаку негде мигнуть.
          requestAnimationFrame(() => requestAnimationFrame(() => _liftCurtain('объём по требованию')));
          setTimeout(() => { try { re.renderViewports([vpId]); } catch (e) { log.debug("[viewport]", e.message); } }, 200);
        } catch (e) { _liftCurtain('ошибка постройки'); log.warn('[switchViewMode] volume:', e); }
      }
    }
  
    return vpId;
  } catch (e) { log.warn(`[viewport] switchViewMode failed:`, e.message); throw e; }
}

// ══════════════════════════════════════════════════════════════
//  HANGING PROTOCOLS
// ══════════════════════════════════════════════════════════════

const _hangingProtocols = [
  {
    id: 'cardiac-ct', name: 'Кардио-КТ',
    match: (info) => {
      let s = 0;
      if (info.modality === 'CT') s += 20;
      if (/heart|cardiac|cardio|коронар|сердц/i.test(info.description || '')) s += 40;
      if (/heart|cardiac|chest/i.test(info.bodyPart || '')) s += 20;
      if (info.phaseCount >= 4) s += 20;
      return s;
    },
    layout: { type: '2x2', viewports: [{ mode: 'axial' }, { mode: 'coronal' }, { mode: 'sagittal' }, { mode: '3d', preset: 'cardiac-3d' }] },
  },
  {
    id: 'chest-ct', name: 'Грудная КТ (MPR)',
    match: (info) => {
      let s = 0;
      if (info.modality === 'CT') s += 20;
      if (/chest|lung|thorax|грудн|лёгк/i.test(info.description || '')) s += 40;
      if (/chest|thorax/i.test(info.bodyPart || '')) s += 30;
      return s;
    },
    layout: { type: 'mpr' },
  },
  {
    id: 'brain-ct', name: 'КТ головного мозга',
    match: (info) => {
      let s = 0;
      if (info.modality === 'CT') s += 20;
      if (/brain|head|skull|мозг|голов|череп/i.test(info.description || '')) s += 50;
      if (/head|brain/i.test(info.bodyPart || '')) s += 30;
      return s;
    },
    layout: { type: 'mpr', viewports: [{ wwwc: { ww: 80, wc: 40 } }, {}, {}, { preset: 'ct-bone-clean' }] },
  },
  {
    id: 'abdomen-ct', name: 'КТ ОБП',
    match: (info) => {
      let s = 0;
      if (info.modality === 'CT') s += 20;
      if (/abdomen|pelvis|liver|kidney|брюшн|живот|печен/i.test(info.description || '')) s += 40;
      return s;
    },
    layout: { type: 'mpr', viewports: [{ wwwc: { ww: 350, wc: 40 } }, {}, {}, { preset: 'ct-soft-tissue' }] },
  },
  { id: 'default', name: 'По умолчанию', match: () => 5, layout: { type: '1x1' } },
];

export function matchHangingProtocol(studyInfo) {
  let best = _hangingProtocols[_hangingProtocols.length - 1], bestScore = 0;
  for (const p of _hangingProtocols) { const s = p.match(studyInfo); if (s > bestScore) { bestScore = s; best = p; } }
  return { protocol: best, score: bestScore };
}

export async function applyHangingProtocol(containerId, studyInfo, loadOpts = {}) {
  try {
    const { protocol, score } = matchHangingProtocol(studyInfo);
    log.debug(`[HP] Matched: "${protocol.name}" (score=${score})`);
    const vpIds = await setupViewports(containerId, protocol.layout.type);
    if (protocol.layout.viewports) {
      const re = getRenderingEngine();
      for (let i = 0; i < protocol.layout.viewports.length && i < vpIds.length; i++) {
        const cfg = protocol.layout.viewports[i], vp = re?.getViewport(vpIds[i]);
        if (!vp) continue;
        if (cfg.wwwc && vp.type !== ViewportType.VOLUME_3D) {
          try { const { lower, upper } = cornerstone.utilities.windowLevel.toLowHighRange(cfg.wwwc.ww, cfg.wwwc.wc); vp.setProperties({ voiRange: { lower, upper } }); } catch (e) { log.debug("[viewport]", e.message); }
        }
        if (cfg.preset && vp.type === ViewportType.VOLUME_3D) try { apply3DPreset(vp, cfg.preset); } catch (e) { log.debug("[viewport]", e.message); }
      }
    }
    events.emit(EVENTS.HANGING_PROTOCOL_APPLIED, { protocol, score, viewportIds: vpIds });
    showToast(`📐 ${protocol.name}`, 'info', 2000);
    return { protocol, viewportIds: vpIds };
  } catch (e) { log.warn(`[viewport] applyHangingProtocol failed:`, e.message); return {}; }
}

export function addHangingProtocol(protocol) {
  if (!protocol?.id || !protocol?.match || !protocol?.layout) return;
  _hangingProtocols.splice(_hangingProtocols.length - 1, 0, protocol);
}

export function listHangingProtocols() {
  return _hangingProtocols.map(p => ({ id: p.id, name: p.name }));
}

// ══════════════════════════════════════════════════════════════
//  ADAPTIVE QUALITY (reduce resolution during 3D interaction)
// ══════════════════════════════════════════════════════════════

const _adaptiveTargets = new Map();

export function enableAdaptiveQuality(viewportId) {
  const vpId = viewportId || find3DViewportId();
  const vp = getRenderingEngine()?.getViewport(vpId);
  if (!vp || vp.type !== ViewportType.VOLUME_3D) return;
  disableAdaptiveQuality(vpId);

  const el = vp.element;
  if (!el) return;

  let interacting = false, timer = null;

  const setRes = (scale) => {
    try {
      const mapper = (vp.getDefaultActor?.()?.actor)?.getMapper?.();
      if (mapper?.setSampleDistance) {
        if (!mapper._baseSampleDistance) mapper._baseSampleDistance = mapper.getSampleDistance?.() || 1.0;
        mapper.setSampleDistance(mapper._baseSampleDistance / scale);
      }
      el.style.imageRendering = scale < 1 ? 'auto' : '';
    } catch (e) { log.debug("[viewport]", e.message); }
  };

  const start = () => { if (interacting) return; interacting = true; clearTimeout(timer); setRes(0.5); };
  const end = () => { clearTimeout(timer); timer = setTimeout(() => { interacting = false; setRes(1.0); try { vp.render(); getCinematicRenderer()?.forceRender(); } catch (e) { log.debug("[viewport]", e.message); } }, 350); };

  const onDown = () => start();
  const onMove = (e) => { if (e.buttons) start(); };
  const onUp = () => end();
  const onWheel = () => { start(); end(); };

  el.addEventListener('pointerdown', onDown);
  el.addEventListener('pointermove', onMove);
  el.addEventListener('pointerup', onUp);
  el.addEventListener('pointerleave', onUp);
  el.addEventListener('wheel', onWheel, { passive: true });

  _adaptiveTargets.set(vpId, { listeners: [['pointerdown', onDown], ['pointermove', onMove], ['pointerup', onUp], ['pointerleave', onUp], ['wheel', onWheel]], el, timer });
  log.debug(`[Adaptive] Enabled for ${vpId}`);
}

export function disableAdaptiveQuality(viewportId) {
  const vpId = viewportId || find3DViewportId();
  const entry = _adaptiveTargets.get(vpId);
  if (!entry) return;
  clearTimeout(entry.timer);
  for (const [evt, h] of entry.listeners) entry.el.removeEventListener(evt, h);
  entry.el.style.imageRendering = '';
  _adaptiveTargets.delete(vpId);
}

// ══════════════════════════════════════════════════════════════
//  MULTI-VOLUME OVERLAY (PET/CT fusion)
// ══════════════════════════════════════════════════════════════

// [v7.0.61] Проверка всех возможных полей uid
function _getCurrentVolumeIdFromViewports() {
  const re = getRenderingEngine();
  for (const vpId of getViewportIds()) {
    const vp = re?.getViewport(vpId);
    if (!vp || vp.type === ViewportType.STACK) continue;
    const actors = vp.getActors?.();
    if (actors?.length) {
      const id = actors[0].uid || actors[0].referencedId || actors[0].referenceId || actors[0].volumeId;
      if (id) return id;
    }
  }
  return null;
}

export async function addVolumeOverlay(overlayVolumeId, imageIds, viewportIds, opts = {}) {
  const re = getRenderingEngine();
  if (!re) return null;
  const opacity = opts.opacity ?? 0.5;
  const fullId = overlayVolumeId.startsWith('cornerstoneStreamingImageVolume:') ? overlayVolumeId : `cornerstoneStreamingImageVolume:${overlayVolumeId}`;
  if (!opts.isRecovery && Array.isArray(imageIds) && imageIds.length) {
    rememberVolume(fullId, imageIds, viewportIds, opts, 'overlay');
    try { memoryGuardian.lock(imageIds); } catch (e) { log.debug('[Overlay] lock:', e.message); }
  }
  try {
    try { cornerstone.cache.removeVolumeLoadObject(fullId); } catch (e) { log.debug("[viewport]", e.message); }
    const ov = await cornerstone.volumeLoader.createAndCacheVolume(fullId, { imageIds });
    ov.load();
    await new Promise(r => setTimeout(r, 300));
    const validIds = viewportIds.filter(id => { const vp = re.getViewport(id); return vp && vp.type !== ViewportType.STACK; });
    const mainVolId = _getCurrentVolumeIdFromViewports();
    for (const vpId of validIds) {
      try {
        await cornerstone.setVolumesForViewports(re, [
          { volumeId: mainVolId },
          { volumeId: fullId, callback: ({ volumeActor }) => { try { const opFn = volumeActor?.getProperty?.()?.getScalarOpacity?.(0); if (opFn) { opFn.removeAllPoints(); opFn.addPoint(0, 0); opFn.addPoint(1, opacity); } } catch (e) { log.debug("[viewport]", e.message); } } },
        ], [vpId]);
      } catch (e) { log.warn(`[Overlay] ${vpId}:`, e.message); }
    }
    re.renderViewports(validIds);
    events.emit(EVENTS.OVERLAY_ADDED, { overlayVolumeId: fullId, viewportIds: validIds });
    return ov;
  } catch (e) { log.warn('[Overlay]', e.message); return null; }
}

export function removeVolumeOverlay(overlayVolumeId, viewportIds) {
  const re = getRenderingEngine();
  if (!re) return;
  const fullId = overlayVolumeId.startsWith('cornerstoneStreamingImageVolume:') ? overlayVolumeId : `cornerstoneStreamingImageVolume:${overlayVolumeId}`;
  const ids = viewportIds || getViewportIds();
  for (const vpId of ids) { try { re.getViewport(vpId)?.removeVolumeActors?.([fullId]); } catch (e) { log.debug("[viewport]", e.message); } }
  try { cornerstone.cache.removeVolumeLoadObject(fullId); } catch (e) { log.debug("[viewport]", e.message); }
  try { forgetVolume(fullId); } catch (e) { log.debug("[viewport]", e.message); }
  re.renderViewports(ids);
  events.emit(EVENTS.OVERLAY_REMOVED, { overlayVolumeId: fullId });
}

export function setOverlayOpacity(overlayVolumeId, opacity) {
  const re = getRenderingEngine();
  if (!re) return;
  const fullId = overlayVolumeId.startsWith('cornerstoneStreamingImageVolume:') ? overlayVolumeId : `cornerstoneStreamingImageVolume:${overlayVolumeId}`;
  for (const vpId of getViewportIds()) {
    try {
      const actors = re.getViewport(vpId)?.getActors?.();
      if (!actors) continue;
      for (const a of actors) {
        if (a.uid === fullId || a.referencedId === fullId) {
          const opFn = a.actor?.getProperty?.()?.getScalarOpacity?.(0);
          if (opFn) { opFn.removeAllPoints(); opFn.addPoint(0, 0); opFn.addPoint(1, opacity); }
          break;
        }
      }
    } catch (e) { log.debug("[viewport]", e.message); }
  }
  re.renderViewports(getViewportIds());
}

export function setOverlayColormap(overlayVolumeId, colormapName) {
  const re = getRenderingEngine();
  if (!re || !overlayVolumeId || !colormapName) return;
  const fullId = overlayVolumeId.startsWith('cornerstoneStreamingImageVolume:') ? overlayVolumeId : `cornerstoneStreamingImageVolume:${overlayVolumeId}`;
  for (const vpId of getViewportIds()) {
    try {
      const vp = re.getViewport(vpId);
      if (vp?.setProperties) { vp.setProperties({ colormap: { name: colormapName } }, fullId); vp.render?.(); }
    } catch (e) { log.debug("[viewport]", e.message); }
  }
}

// ══════════════════════════════════════════════════════════════
//  VIEWER API REGISTRATION (all sub-modules)
// ══════════════════════════════════════════════════════════════

registerAPIs({
  recoveryReport: () => { try { return showRecoveryReport(); } catch (e) { return null; } },
  recoveryReportText: () => { try { return getRecoveryReport(); } catch (e) { return ''; } },
  zoomViewport, cinematicToggle: toggleCinematic, cinematicPreset: setCinematicPreset, cinematicPanel: mountCinematicPanel,
  pushCameraState, popCameraState, redoCameraState, saveCameraPreset, loadCameraPreset, startAutoRotate, stopAutoRotate, animateToStandardView, flyAlongPath,
  adjustCamera3D, rotateCameraRoll,
  screenshotViewport, downloadScreenshot, screenshotAll, copyScreenshotToClipboard, createComparisonStrip, batchExportSlices,
  enableHUProbe, disableHUProbe, computeCalciumScore, computeCircleROIStats, classifyTissue, computeLineProfile, computeVolumeStats,
  startCine, stopCine, getCineState, setCineSpeed, pauseCine, resumeCine, toggleCinePlayback, setCineRange, seekCine, getCineStats,
  enablePositionSync, disablePositionSync, enableVoiceControl, disableVoiceControl, addVoiceCommand,
  enableGestures, enableAllGestures, init4DViewer, setCardiacPhase, nextCardiacPhase, prevCardiacPhase, start4DPlayback, stop4DPlayback, get4DState,
  jumpToSystole, jumpToDiastole, set4DSpeed, getPhaseTimeline, preloadAllPhases,
  startSmartCompare, stopSmartCompare, enableKeyboardShortcuts, disableKeyboardShortcuts, addKeyboardShortcut, listKeyboardShortcuts,
  getWLPresets, applyWLPreset, autoWindowLevel, addBookmark, goToBookmark, removeBookmark, getBookmarks, navigateBookmarks,
  toggleMagnifier, enableMagnifier, disableMagnifier, toggleRulerGrid, showRulerGrid, removeRulerGrid,
  showInfoOverlay, hideInfoOverlay, showCrosshairOverlay, hideCrosshairOverlay, toggleCrosshairOverlay, showPictureInPicture, hidePictureInPicture,
  setSlabProjection, getSlabState, cycleSlabProjection, adjustSlabThickness, applySlabPreset, applyClipPreset, applyDualEnergyMode,
  setVolumeClipping, getVolumeClipping, animateWindowLevel, animatePresetTransition,
  startSessionRecording, stopSessionRecording, replaySession, exportSessionHTML, getAnnotationStats, startStudyTimer, stopStudyTimer,
  matchHangingProtocol, applyHangingProtocol, enableAdaptiveQuality, disableAdaptiveQuality,
  addVolumeOverlay, removeVolumeOverlay, setOverlayOpacity, setOverlayColormap,
});

/** [FIX] Очистка глобальных слушателей */
export function destroyViewport() {
  if (_ac) { _ac.abort(); _ac = null; }
}